import { Deserializer as Ze, AccountAuthenticator as Ye, Secp256r1PublicKey as qt, Ed25519PublicKey as Fe, AuthenticationKey as ze, generateSigningMessageForTransaction as We, WebAuthnSignature as Xe, AccountAuthenticatorSingleKey as Je, AnyPublicKey as Qe, AnySignature as tn, AccountAddress as en, Network as nn, Aptos as rn, AptosConfig as sn } from "@aptos-labs/ts-sdk";
import { Buffer as on } from "buffer";
const an = {
  passkey: {
    /** Mainnet passkey contract address (not yet deployed) */
    "aptos-mainnet": "",
    /** Testnet passkey contract address for development and testing */
    "aptos-testnet": "0x9bb469e57da81071c7961259445b87f85434da79fcacf06b2bc670556ce94924"
  }
}, cn = Object.freeze({
  /** Maximum gas amount in gas units (sufficient for most passkey operations) */
  maxGasAmount: 1e4
}), un = "https://dolphin.caas.xyz", ln = {
  /** Production mainnet API endpoint */
  mainnet: "https://api.caas.xyz",
  /** Testnet API endpoint for development and testing */
  testnet: "https://api-test.caas.xyz"
};
function tt(e, t) {
  if (t == null)
    return e;
  let n;
  if (t instanceof Error)
    n = t.message;
  else if (typeof t == "string")
    n = t;
  else if (typeof t == "object")
    try {
      n = JSON.stringify(t);
    } catch {
      n = String(t);
    }
  else
    n = String(t);
  return n !== "" ? `${e}: ${n}` : e;
}
class N extends Error {
  /**
   * Create a new PasskeyError instance
   * 
   * @param message - Human-readable error description (should include specific details)
   * @param code - Optional structured error code for programmatic handling
   * @param cause - Optional original error/data that caused this error (can be Error, string, or any object)
   */
  constructor(t, n, r) {
    super(t), this.code = n, this.cause = r, this.name = "PasskeyError", r !== void 0 && "cause" in Error.prototype && (this.cause = r);
  }
}
async function dn(e, t = 3, n = 1e3) {
  let r;
  for (let s = 1; s <= t; s++)
    try {
      return await e();
    } catch (o) {
      if (r = o instanceof Error ? o : new Error(String(o)), s === t)
        throw new N(
          `Operation failed after ${t} attempts: ${r.message}`,
          "RETRY_EXHAUSTED",
          r
        );
      const i = n * Math.pow(2, s - 1);
      await new Promise((a) => setTimeout(a, i));
    }
  throw r;
}
async function Et(e, t, n = 3) {
  return dn(async () => {
    const r = await fetch(e, t);
    if (!r.ok)
      throw new N(
        `HTTP ${r.status}: ${r.statusText}`,
        "HTTP_ERROR"
      );
    return r;
  }, n);
}
const Ot = "caas_address", Bt = "caas_access_token";
class fn {
  /**
   * Initialize the login manager
   *
   * @param config - CAAS SDK configuration containing API endpoints and settings
   */
  constructor(t) {
    this._core = t, this.initializeFromStorage();
  }
  currentAddress = "";
  accessToken = null;
  sessionCheckInterval = null;
  authStateListeners = [];
  /**
   * Authenticate user with provided credentials
   *
   * Validates user credentials against the authentication service and
   * establishes a new user session with access tokens.
   *
   * @param credentials - User login credentials
   * @param options - Optional login configuration
   * @returns Promise resolving to login response with session and tokens
   * @throws {PasskeyError} When authentication fails or invalid credentials
   */
  async login(t) {
    if (!t.address)
      throw new N("Address is required", "MISSING_ADDRESS");
    if (!t.signMessage)
      throw new N("Sign message is required", "MISSING_SIGN_MESSAGE");
    if (!t.publicKey)
      throw new N("Public key is required", "MISSING_PUBLIC_KEY");
    if (!t.signature)
      throw new N("Signature is required", "MISSING_SIGNATURE");
    try {
      const r = await (await Et(`${this._core.Endpoint}/user/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          ...t
        })
      })).text();
      if (!r)
        throw new N(r || "Login failed", "LOGIN_FAILED");
      return this.setSession(t.address, r), this.emitAuthStateChange({
        type: "login",
        address: t.address,
        timestamp: /* @__PURE__ */ new Date()
      }), {
        address: t.address
      };
    } catch (n) {
      throw new N(
        tt("Login operation failed", n),
        "LOGIN_ERROR",
        n instanceof Error ? n : new Error(String(n))
      );
    }
  }
  /**
   * Logout current user and clear session
   *
   * Invalidates the current session, clears stored tokens, and
   * notifies the backend service about session termination.
   *
   * @returns Promise resolving when logout is complete
   * @throws {PasskeyError} When logout operation fails
   */
  async logout() {
    this.clearSession(), this.emitAuthStateChange({
      type: "logout",
      timestamp: /* @__PURE__ */ new Date()
    });
  }
  /**
   * Check if user is currently authenticated
   *
   * @returns True if user has valid session and tokens
   */
  isAuthenticated() {
    return !!(this.currentAddress && this.accessToken);
  }
  /**
   * Check if user is currently authenticated with the given address
   *
   * @param address - User's wallet address
   * @returns True if user has valid session and tokens with the given address
   */
  isAuthenticatedWithAddress(t) {
    return this.currentAddress === t && this.isAuthenticated();
  }
  /**
   * Get current user address information
   *
   * @returns Current user session or null if not authenticated
   */
  getCurrentAddress() {
    return this.isAuthenticated() ? this.currentAddress : null;
  }
  /**
   * Get current access token
   *
   * @returns Current access token or null if not authenticated
   */
  getAccessToken() {
    return this.isAuthenticated() ? this.accessToken : null;
  }
  /**
   * Get user's passkey list
   *
   * @param args - User's passkey list arguments
   * @param args.rpId - Relying Party ID (domain name)
   * @returns Promise resolving to array of user's passkeys
   * @throws {PasskeyError} When passkey fetch fails or user not authenticated
   */
  async getPasskeys(t) {
    return await (await Et(`${this._core.Endpoint}/user/passkey/${t.rpId}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${this.accessToken}`,
        "Content-Type": "application/json"
      }
    })).json();
  }
  /**
   * Get project information by Relying Party ID
   *
   * @param args - Request arguments
   * @param args.rpId - Relying Party ID (domain name)
   * @returns Promise resolving to project information
   * @throws {PasskeyError} When project fetch fails or user not authenticated
   */
  async getProjectInfo(t) {
    return await (await Et(`${this._core.Endpoint}/user/project/${t.rpId}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${this.accessToken}`,
        "Content-Type": "application/json"
      }
    })).json();
  }
  /**
   * Clean up resources and stop session monitoring
   */
  destroy() {
    this.sessionCheckInterval && (clearInterval(this.sessionCheckInterval), this.sessionCheckInterval = null), this.authStateListeners = [];
  }
  // Private helper methods
  /**
   * Set the current user session with address and access token
   *
   * @param address - User's wallet address
   * @param accessToken - Authentication access token
   */
  setSession(t, n) {
    this.currentAddress = t, this.accessToken = n, this.saveToStorage();
  }
  /**
   * Clear the current user session and tokens
   */
  clearSession() {
    this.currentAddress = "", this.accessToken = "", this.clearStorage();
  }
  /**
   * Emit authentication state change event to all listeners
   *
   * @param event - Authentication state change event data
   */
  emitAuthStateChange(t) {
    this.authStateListeners.forEach((n) => {
      try {
        n(t);
      } catch (r) {
        console.error("Error in auth state listener:", r);
      }
    });
  }
  /**
   * Initialize session data from local storage
   * Restores user session if valid data exists in storage
   */
  initializeFromStorage() {
    try {
      const t = localStorage.getItem(Ot), n = localStorage.getItem(Bt);
      if (t && n) {
        const r = t;
        r ? (this.currentAddress = r, this.accessToken = n) : this.clearStorage();
      }
    } catch (t) {
      console.warn("Failed to restore session from storage:", t), this.clearStorage();
    }
  }
  /**
   * Save current session data to local storage
   */
  saveToStorage() {
    try {
      this.currentAddress && this.accessToken && (localStorage.setItem(Ot, this.currentAddress), localStorage.setItem(Bt, this.accessToken));
    } catch (t) {
      console.warn("Failed to save session to storage:", t);
    }
  }
  /**
   * Clear session data from local storage
   */
  clearStorage() {
    try {
      localStorage.removeItem(Ot), localStorage.removeItem(Bt);
    } catch (t) {
      console.warn("Failed to clear session storage:", t);
    }
  }
}
class hn {
  /**
   * Initialize the transaction simulator
   *
   * @param endpoint - CAAS backend API endpoint for transaction simulation
   */
  constructor(t) {
    this.endpoint = t;
  }
  /**
   * Simulate a multi-agent transaction and get the simulation response
   *
   * Sends the transaction to the backend simulation service to predict
   * execution results and obtain project signer authentication details.
   *
   * @param transaction - Multi-agent transaction to simulate
   * @param options - Optional simulation configuration
   * @returns Promise resolving to simulation response with custom fields
   * @throws {PasskeyError} When simulation request fails or response parsing fails
   */
  async simulateTransaction(t, n) {
    const r = await Et(`${this.endpoint}/v1/transactions/simulate`, {
      method: "POST",
      body: JSON.stringify({
        // transaction: transactionInBCS,
        transaction: t.bcsToHex().toString(),
        estimateGasUnitPrice: n?.estimateGasUnitPrice || !0,
        estimateMaxGasAmount: n?.estimateMaxGasAmount || !0,
        estimatePrioritizedGasUnitPrice: n?.estimatePrioritizedGasUnitPrice || !0
      }),
      headers: {
        "Content-Type": "application/json"
      }
    });
    try {
      return await r.json();
    } catch (s) {
      throw new N(
        tt("Failed to parse simulation response", s),
        "PARSE_ERROR",
        s instanceof Error ? s : new Error(String(s))
      );
    }
  }
  /**
   * Validate simulation result and ensure it contains required data
   *
   * Checks that the simulation was successful and contains the necessary
   * validation signature data for project signer extraction.
   *
   * @param result - Simulation response to validate
   * @throws {PasskeyError} When simulation failed or required data is missing
   */
  validateSimulationResult(t) {
    if (!t.aptosResult?.success)
      throw new N(
        tt("Transaction simulation failed", t.aptosResult?.vm_status),
        "SIMULATION_FAILED"
      );
    const { validationSignature: n, riskAssessment: r } = t.customFields || {};
    if (r?.riskLevel === "high")
      throw new N(r.warnings.join(", "), "HIGH_RISK_LEVEL");
    if (!n?.authenticator)
      throw new N(
        tt("Validation signature authenticator is missing from response"),
        "MISSING_AUTHENTICATOR"
      );
  }
  /**
   * Deserialize authenticator from hex-encoded validation signature
   *
   * Converts the hex-encoded authenticator from the simulation response
   * back into an AccountAuthenticator object for transaction signing.
   *
   * @param authenticator - Hex-encoded authenticator string
   * @returns Deserialized AccountAuthenticator object
   * @throws {PasskeyError} When deserialization fails
   */
  deserializeAuthenticator(t) {
    try {
      const n = new Ze(on.from(t.replace("0x", ""), "hex"));
      return Ye.deserialize(n);
    } catch (n) {
      throw new N(
        tt("Failed to deserialize authenticator", n),
        "DESERIALIZE_ERROR",
        n instanceof Error ? n : new Error(String(n))
      );
    }
  }
  /**
   * Restore an Aptos address from a public key
   *
   * Derives the corresponding Aptos address from a public key using
   * the authentication key derivation process. Supports both secp256r1
   * (passkey) and ed25519 (project signer) key types.
   *
   * @param publicKey - Public key in hex format
   * @param type - Key type: 'secp256r1' for passkeys, 'ed25519' for project signer
   * @returns Derived Aptos address as a string
   * @throws {PasskeyError} When address derivation fails
   */
  restoreAddressFromPublicKey(t, n = "secp256r1") {
    try {
      const r = n === "secp256r1" ? new qt(t) : new Fe(t);
      return ze.fromPublicKey({
        publicKey: r
      }).derivedAddress().toStringLong();
    } catch (r) {
      throw new N(
        tt("Failed to restore address from public key", r),
        "ADDRESS_RESTORE_ERROR",
        r instanceof Error ? r : new Error(String(r))
      );
    }
  }
  /**
   * Extract project signer information from transaction simulation
   *
   * Simulates the transaction and extracts the project signer's authentication
   * details from the simulation response. This provides the necessary
   * authenticator and address information for multi-agent transaction signing.
   *
   * @param transaction - Multi-agent transaction to simulate
   * @param options - Optional simulation configuration
   * @returns Promise resolving to project signer information
   * @throws {PasskeyError} When simulation fails or signer extraction fails
   */
  async getProjectSigner(t, n) {
    const r = await this.simulateTransaction(t, n);
    this.validateSimulationResult(r);
    const { processedAt: s, requestId: o, validationSignature: i } = r.customFields, a = this.deserializeAuthenticator(i.authenticator);
    return {
      processedAt: s,
      requestId: o,
      projectSenderAuthenticator: a
    };
  }
}
const yt = /* @__PURE__ */ BigInt(2 ** 32 - 1), Jt = /* @__PURE__ */ BigInt(32);
function gn(e, t = !1) {
  return t ? { h: Number(e & yt), l: Number(e >> Jt & yt) } : { h: Number(e >> Jt & yt) | 0, l: Number(e & yt) | 0 };
}
function wn(e, t = !1) {
  const n = e.length;
  let r = new Uint32Array(n), s = new Uint32Array(n);
  for (let o = 0; o < n; o++) {
    const { h: i, l: a } = gn(e[o], t);
    [r[o], s[o]] = [i, a];
  }
  return [r, s];
}
const pn = (e, t, n) => e << n | t >>> 32 - n, yn = (e, t, n) => t << n | e >>> 32 - n, mn = (e, t, n) => t << n - 32 | e >>> 64 - n, bn = (e, t, n) => e << n - 32 | t >>> 64 - n;
/*! noble-hashes - MIT License (c) 2022 Paul Miller (paulmillr.com) */
function Mt(e) {
  return e instanceof Uint8Array || ArrayBuffer.isView(e) && e.constructor.name === "Uint8Array";
}
function W(e, t = "") {
  if (!Number.isSafeInteger(e) || e < 0) {
    const n = t && `"${t}" `;
    throw new Error(`${n}expected integer >= 0, got ${e}`);
  }
}
function C(e, t, n = "") {
  const r = Mt(e), s = e?.length, o = t !== void 0;
  if (!r || o && s !== t) {
    const i = n && `"${n}" `, a = o ? ` of length ${t}` : "", c = r ? `length=${s}` : `type=${typeof e}`;
    throw new Error(i + "expected Uint8Array" + a + ", got " + c);
  }
  return e;
}
function de(e) {
  if (typeof e != "function" || typeof e.create != "function")
    throw new Error("Hash must wrapped by utils.createHasher");
  W(e.outputLen), W(e.blockLen);
}
function ct(e, t = !0) {
  if (e.destroyed)
    throw new Error("Hash instance has been destroyed");
  if (t && e.finished)
    throw new Error("Hash#digest() has already been called");
}
function fe(e, t) {
  C(e, void 0, "digestInto() output");
  const n = t.outputLen;
  if (e.length < n)
    throw new Error('"digestInto() output" expected to be of length >=' + n);
}
function En(e) {
  return new Uint32Array(e.buffer, e.byteOffset, Math.floor(e.byteLength / 4));
}
function ut(...e) {
  for (let t = 0; t < e.length; t++)
    e[t].fill(0);
}
function Tt(e) {
  return new DataView(e.buffer, e.byteOffset, e.byteLength);
}
function Y(e, t) {
  return e << 32 - t | e >>> t;
}
const Sn = new Uint8Array(new Uint32Array([287454020]).buffer)[0] === 68;
function An(e) {
  return e << 24 & 4278190080 | e << 8 & 16711680 | e >>> 8 & 65280 | e >>> 24 & 255;
}
function Rn(e) {
  for (let t = 0; t < e.length; t++)
    e[t] = An(e[t]);
  return e;
}
const Qt = Sn ? (e) => e : Rn, he = /* @ts-ignore */ typeof Uint8Array.from([]).toHex == "function" && typeof Uint8Array.fromHex == "function", xn = /* @__PURE__ */ Array.from({ length: 256 }, (e, t) => t.toString(16).padStart(2, "0"));
function It(e) {
  if (C(e), he)
    return e.toHex();
  let t = "";
  for (let n = 0; n < e.length; n++)
    t += xn[e[n]];
  return t;
}
const F = { _0: 48, _9: 57, A: 65, F: 70, a: 97, f: 102 };
function te(e) {
  if (e >= F._0 && e <= F._9)
    return e - F._0;
  if (e >= F.A && e <= F.F)
    return e - (F.A - 10);
  if (e >= F.a && e <= F.f)
    return e - (F.a - 10);
}
function At(e) {
  if (typeof e != "string")
    throw new Error("hex string expected, got " + typeof e);
  if (he)
    return Uint8Array.fromHex(e);
  const t = e.length, n = t / 2;
  if (t % 2)
    throw new Error("hex string expected, got unpadded hex of length " + t);
  const r = new Uint8Array(n);
  for (let s = 0, o = 0; s < n; s++, o += 2) {
    const i = te(e.charCodeAt(o)), a = te(e.charCodeAt(o + 1));
    if (i === void 0 || a === void 0) {
      const c = e[o] + e[o + 1];
      throw new Error('hex string expected, got non-hex character "' + c + '" at index ' + o);
    }
    r[s] = i * 16 + a;
  }
  return r;
}
function et(...e) {
  let t = 0;
  for (let r = 0; r < e.length; r++) {
    const s = e[r];
    C(s), t += s.length;
  }
  const n = new Uint8Array(t);
  for (let r = 0, s = 0; r < e.length; r++) {
    const o = e[r];
    n.set(o, s), s += o.length;
  }
  return n;
}
function ge(e, t = {}) {
  const n = (s, o) => e(o).update(s).digest(), r = e(void 0);
  return n.outputLen = r.outputLen, n.blockLen = r.blockLen, n.create = (s) => e(s), Object.assign(n, t), Object.freeze(n);
}
function we(e = 32) {
  const t = typeof globalThis == "object" ? globalThis.crypto : null;
  if (typeof t?.getRandomValues != "function")
    throw new Error("crypto.getRandomValues must be defined");
  return t.getRandomValues(new Uint8Array(e));
}
const pe = (e) => ({
  oid: Uint8Array.from([6, 9, 96, 134, 72, 1, 101, 3, 4, 2, e])
}), In = BigInt(0), gt = BigInt(1), vn = BigInt(2), _n = BigInt(7), On = BigInt(256), Bn = BigInt(113), ye = [], me = [], be = [];
for (let e = 0, t = gt, n = 1, r = 0; e < 24; e++) {
  [n, r] = [r, (2 * n + 3 * r) % 5], ye.push(2 * (5 * r + n)), me.push((e + 1) * (e + 2) / 2 % 64);
  let s = In;
  for (let o = 0; o < 7; o++)
    t = (t << gt ^ (t >> _n) * Bn) % On, t & vn && (s ^= gt << (gt << BigInt(o)) - gt);
  be.push(s);
}
const Ee = wn(be, !0), Tn = Ee[0], Nn = Ee[1], ee = (e, t, n) => n > 32 ? mn(e, t, n) : pn(e, t, n), ne = (e, t, n) => n > 32 ? bn(e, t, n) : yn(e, t, n);
function Cn(e, t = 24) {
  const n = new Uint32Array(10);
  for (let r = 24 - t; r < 24; r++) {
    for (let i = 0; i < 10; i++)
      n[i] = e[i] ^ e[i + 10] ^ e[i + 20] ^ e[i + 30] ^ e[i + 40];
    for (let i = 0; i < 10; i += 2) {
      const a = (i + 8) % 10, c = (i + 2) % 10, l = n[c], u = n[c + 1], g = ee(l, u, 1) ^ n[a], p = ne(l, u, 1) ^ n[a + 1];
      for (let y = 0; y < 50; y += 10)
        e[i + y] ^= g, e[i + y + 1] ^= p;
    }
    let s = e[2], o = e[3];
    for (let i = 0; i < 24; i++) {
      const a = me[i], c = ee(s, o, a), l = ne(s, o, a), u = ye[i];
      s = e[u], o = e[u + 1], e[u] = c, e[u + 1] = l;
    }
    for (let i = 0; i < 50; i += 10) {
      for (let a = 0; a < 10; a++)
        n[a] = e[i + a];
      for (let a = 0; a < 10; a++)
        e[i + a] ^= ~n[(a + 2) % 10] & n[(a + 4) % 10];
    }
    e[0] ^= Tn[r], e[1] ^= Nn[r];
  }
  ut(n);
}
class jt {
  state;
  pos = 0;
  posOut = 0;
  finished = !1;
  state32;
  destroyed = !1;
  blockLen;
  suffix;
  outputLen;
  enableXOF = !1;
  rounds;
  // NOTE: we accept arguments in bytes instead of bits here.
  constructor(t, n, r, s = !1, o = 24) {
    if (this.blockLen = t, this.suffix = n, this.outputLen = r, this.enableXOF = s, this.rounds = o, W(r, "outputLen"), !(0 < t && t < 200))
      throw new Error("only keccak-f1600 function is supported");
    this.state = new Uint8Array(200), this.state32 = En(this.state);
  }
  clone() {
    return this._cloneInto();
  }
  keccak() {
    Qt(this.state32), Cn(this.state32, this.rounds), Qt(this.state32), this.posOut = 0, this.pos = 0;
  }
  update(t) {
    ct(this), C(t);
    const { blockLen: n, state: r } = this, s = t.length;
    for (let o = 0; o < s; ) {
      const i = Math.min(n - this.pos, s - o);
      for (let a = 0; a < i; a++)
        r[this.pos++] ^= t[o++];
      this.pos === n && this.keccak();
    }
    return this;
  }
  finish() {
    if (this.finished)
      return;
    this.finished = !0;
    const { state: t, suffix: n, pos: r, blockLen: s } = this;
    t[r] ^= n, (n & 128) !== 0 && r === s - 1 && this.keccak(), t[s - 1] ^= 128, this.keccak();
  }
  writeInto(t) {
    ct(this, !1), C(t), this.finish();
    const n = this.state, { blockLen: r } = this;
    for (let s = 0, o = t.length; s < o; ) {
      this.posOut >= r && this.keccak();
      const i = Math.min(r - this.posOut, o - s);
      t.set(n.subarray(this.posOut, this.posOut + i), s), this.posOut += i, s += i;
    }
    return t;
  }
  xofInto(t) {
    if (!this.enableXOF)
      throw new Error("XOF is not possible for this instance");
    return this.writeInto(t);
  }
  xof(t) {
    return W(t), this.xofInto(new Uint8Array(t));
  }
  digestInto(t) {
    if (fe(t, this), this.finished)
      throw new Error("digest() was already called");
    return this.writeInto(t), this.destroy(), t;
  }
  digest() {
    return this.digestInto(new Uint8Array(this.outputLen));
  }
  destroy() {
    this.destroyed = !0, ut(this.state);
  }
  _cloneInto(t) {
    const { blockLen: n, suffix: r, outputLen: s, rounds: o, enableXOF: i } = this;
    return t ||= new jt(n, r, s, i, o), t.state32.set(this.state32), t.pos = this.pos, t.posOut = this.posOut, t.finished = this.finished, t.rounds = o, t.suffix = r, t.outputLen = s, t.enableXOF = i, t.destroyed = this.destroyed, t;
  }
}
const Ln = (e, t, n, r = {}) => ge(() => new jt(t, e, n), r), kn = /* @__PURE__ */ Ln(
  6,
  136,
  32,
  /* @__PURE__ */ pe(8)
);
function G(e) {
  const t = new Uint8Array(e);
  let n = "";
  for (const s of t)
    n += String.fromCharCode(s);
  return btoa(n).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}
function it(e) {
  const t = e.replace(/-/g, "+").replace(/_/g, "/"), n = (4 - t.length % 4) % 4, r = t.padEnd(t.length + n, "="), s = atob(r), o = new ArrayBuffer(s.length), i = new Uint8Array(o);
  for (let a = 0; a < s.length; a++)
    i[a] = s.charCodeAt(a);
  return o;
}
function $t() {
  return Pn.stubThis(globalThis?.PublicKeyCredential !== void 0 && typeof globalThis.PublicKeyCredential == "function");
}
const Pn = {
  stubThis: (e) => e
};
function Se(e) {
  const { id: t } = e;
  return {
    ...e,
    id: it(t),
    /**
     * `descriptor.transports` is an array of our `AuthenticatorTransportFuture` that includes newer
     * transports that TypeScript's DOM lib is ignorant of. Convince TS that our list of transports
     * are fine to pass to WebAuthn since browsers will recognize the new value.
     */
    transports: e.transports
  };
}
function Ae(e) {
  return (
    // Consider localhost valid as well since it's okay wrt Secure Contexts
    e === "localhost" || /^([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/i.test(e)
  );
}
class H extends Error {
  constructor({ message: t, code: n, cause: r, name: s }) {
    super(t, { cause: r }), Object.defineProperty(this, "code", {
      enumerable: !0,
      configurable: !0,
      writable: !0,
      value: void 0
    }), this.name = s ?? r.name, this.code = n;
  }
}
function Un({ error: e, options: t }) {
  const { publicKey: n } = t;
  if (!n)
    throw Error("options was missing required publicKey property");
  if (e.name === "AbortError") {
    if (t.signal instanceof AbortSignal)
      return new H({
        message: "Registration ceremony was sent an abort signal",
        code: "ERROR_CEREMONY_ABORTED",
        cause: e
      });
  } else if (e.name === "ConstraintError") {
    if (n.authenticatorSelection?.requireResidentKey === !0)
      return new H({
        message: "Discoverable credentials were required but no available authenticator supported it",
        code: "ERROR_AUTHENTICATOR_MISSING_DISCOVERABLE_CREDENTIAL_SUPPORT",
        cause: e
      });
    if (
      // @ts-ignore: `mediation` doesn't yet exist on CredentialCreationOptions but it's possible as of Sept 2024
      t.mediation === "conditional" && n.authenticatorSelection?.userVerification === "required"
    )
      return new H({
        message: "User verification was required during automatic registration but it could not be performed",
        code: "ERROR_AUTO_REGISTER_USER_VERIFICATION_FAILURE",
        cause: e
      });
    if (n.authenticatorSelection?.userVerification === "required")
      return new H({
        message: "User verification was required but no available authenticator supported it",
        code: "ERROR_AUTHENTICATOR_MISSING_USER_VERIFICATION_SUPPORT",
        cause: e
      });
  } else {
    if (e.name === "InvalidStateError")
      return new H({
        message: "The authenticator was previously registered",
        code: "ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED",
        cause: e
      });
    if (e.name === "NotAllowedError")
      return new H({
        message: e.message,
        code: "ERROR_PASSTHROUGH_SEE_CAUSE_PROPERTY",
        cause: e
      });
    if (e.name === "NotSupportedError")
      return n.pubKeyCredParams.filter((s) => s.type === "public-key").length === 0 ? new H({
        message: 'No entry in pubKeyCredParams was of type "public-key"',
        code: "ERROR_MALFORMED_PUBKEYCREDPARAMS",
        cause: e
      }) : new H({
        message: "No available authenticator supported any of the specified pubKeyCredParams algorithms",
        code: "ERROR_AUTHENTICATOR_NO_SUPPORTED_PUBKEYCREDPARAMS_ALG",
        cause: e
      });
    if (e.name === "SecurityError") {
      const r = globalThis.location.hostname;
      if (Ae(r)) {
        if (n.rp.id !== r)
          return new H({
            message: `The RP ID "${n.rp.id}" is invalid for this domain`,
            code: "ERROR_INVALID_RP_ID",
            cause: e
          });
      } else return new H({
        message: `${globalThis.location.hostname} is an invalid domain`,
        code: "ERROR_INVALID_DOMAIN",
        cause: e
      });
    } else if (e.name === "TypeError") {
      if (n.user.id.byteLength < 1 || n.user.id.byteLength > 64)
        return new H({
          message: "User ID was not between 1 and 64 characters",
          code: "ERROR_INVALID_USER_ID_LENGTH",
          cause: e
        });
    } else if (e.name === "UnknownError")
      return new H({
        message: "The authenticator was unable to process the specified options, or could not create a new credential",
        code: "ERROR_AUTHENTICATOR_GENERAL_ERROR",
        cause: e
      });
  }
  return e;
}
class Dn {
  constructor() {
    Object.defineProperty(this, "controller", {
      enumerable: !0,
      configurable: !0,
      writable: !0,
      value: void 0
    });
  }
  createNewAbortSignal() {
    if (this.controller) {
      const n = new Error("Cancelling existing WebAuthn API call for new one");
      n.name = "AbortError", this.controller.abort(n);
    }
    const t = new AbortController();
    return this.controller = t, t.signal;
  }
  cancelCeremony() {
    if (this.controller) {
      const t = new Error("Manually cancelling existing WebAuthn API call");
      t.name = "AbortError", this.controller.abort(t), this.controller = void 0;
    }
  }
}
const Re = new Dn(), Hn = ["cross-platform", "platform"];
function xe(e) {
  if (e && !(Hn.indexOf(e) < 0))
    return e;
}
async function Kn(e) {
  !e.optionsJSON && e.challenge && (console.warn("startRegistration() was not called correctly. It will try to continue with the provided options, but this call should be refactored to use the expected call structure instead. See https://simplewebauthn.dev/docs/packages/browser#typeerror-cannot-read-properties-of-undefined-reading-challenge for more information."), e = { optionsJSON: e });
  const { optionsJSON: t, useAutoRegister: n = !1 } = e;
  if (!$t())
    throw new Error("WebAuthn is not supported in this browser");
  const r = {
    ...t,
    challenge: it(t.challenge),
    user: {
      ...t.user,
      id: it(t.user.id)
    },
    excludeCredentials: t.excludeCredentials?.map(Se)
  }, s = {};
  n && (s.mediation = "conditional"), s.publicKey = r, s.signal = Re.createNewAbortSignal();
  let o;
  try {
    o = await navigator.credentials.create(s);
  } catch (A) {
    throw Un({ error: A, options: s });
  }
  if (!o)
    throw new Error("Registration was not completed");
  const { id: i, rawId: a, response: c, type: l } = o;
  let u;
  typeof c.getTransports == "function" && (u = c.getTransports());
  let g;
  if (typeof c.getPublicKeyAlgorithm == "function")
    try {
      g = c.getPublicKeyAlgorithm();
    } catch (A) {
      Nt("getPublicKeyAlgorithm()", A);
    }
  let p;
  if (typeof c.getPublicKey == "function")
    try {
      const A = c.getPublicKey();
      A !== null && (p = G(A));
    } catch (A) {
      Nt("getPublicKey()", A);
    }
  let y;
  if (typeof c.getAuthenticatorData == "function")
    try {
      y = G(c.getAuthenticatorData());
    } catch (A) {
      Nt("getAuthenticatorData()", A);
    }
  return {
    id: i,
    rawId: G(a),
    response: {
      attestationObject: G(c.attestationObject),
      clientDataJSON: G(c.clientDataJSON),
      transports: u,
      publicKeyAlgorithm: g,
      publicKey: p,
      authenticatorData: y
    },
    type: l,
    clientExtensionResults: o.getClientExtensionResults(),
    authenticatorAttachment: xe(o.authenticatorAttachment)
  };
}
function Nt(e, t) {
  console.warn(`The browser extension that intercepted this WebAuthn API call incorrectly implemented ${e}. You should report this error to them.
`, t);
}
function qn() {
  if (!$t())
    return Ct.stubThis(new Promise((t) => t(!1)));
  const e = globalThis.PublicKeyCredential;
  return e?.isConditionalMediationAvailable === void 0 ? Ct.stubThis(new Promise((t) => t(!1))) : Ct.stubThis(e.isConditionalMediationAvailable());
}
const Ct = {
  stubThis: (e) => e
};
function Mn({ error: e, options: t }) {
  const { publicKey: n } = t;
  if (!n)
    throw Error("options was missing required publicKey property");
  if (e.name === "AbortError") {
    if (t.signal instanceof AbortSignal)
      return new H({
        message: "Authentication ceremony was sent an abort signal",
        code: "ERROR_CEREMONY_ABORTED",
        cause: e
      });
  } else {
    if (e.name === "NotAllowedError")
      return new H({
        message: e.message,
        code: "ERROR_PASSTHROUGH_SEE_CAUSE_PROPERTY",
        cause: e
      });
    if (e.name === "SecurityError") {
      const r = globalThis.location.hostname;
      if (Ae(r)) {
        if (n.rpId !== r)
          return new H({
            message: `The RP ID "${n.rpId}" is invalid for this domain`,
            code: "ERROR_INVALID_RP_ID",
            cause: e
          });
      } else return new H({
        message: `${globalThis.location.hostname} is an invalid domain`,
        code: "ERROR_INVALID_DOMAIN",
        cause: e
      });
    } else if (e.name === "UnknownError")
      return new H({
        message: "The authenticator was unable to process the specified options, or could not create a new assertion signature",
        code: "ERROR_AUTHENTICATOR_GENERAL_ERROR",
        cause: e
      });
  }
  return e;
}
async function jn(e) {
  !e.optionsJSON && e.challenge && (console.warn("startAuthentication() was not called correctly. It will try to continue with the provided options, but this call should be refactored to use the expected call structure instead. See https://simplewebauthn.dev/docs/packages/browser#typeerror-cannot-read-properties-of-undefined-reading-challenge for more information."), e = { optionsJSON: e });
  const { optionsJSON: t, useBrowserAutofill: n = !1, verifyBrowserAutofillInput: r = !0 } = e;
  if (!$t())
    throw new Error("WebAuthn is not supported in this browser");
  let s;
  t.allowCredentials?.length !== 0 && (s = t.allowCredentials?.map(Se));
  const o = {
    ...t,
    challenge: it(t.challenge),
    allowCredentials: s
  }, i = {};
  if (n) {
    if (!await qn())
      throw Error("Browser does not support WebAuthn autofill");
    if (document.querySelectorAll("input[autocomplete$='webauthn']").length < 1 && r)
      throw Error('No <input> with "webauthn" as the only or last value in its `autocomplete` attribute was detected');
    i.mediation = "conditional", o.allowCredentials = [];
  }
  i.publicKey = o, i.signal = Re.createNewAbortSignal();
  let a;
  try {
    a = await navigator.credentials.get(i);
  } catch (y) {
    throw Mn({ error: y, options: i });
  }
  if (!a)
    throw new Error("Authentication was not completed");
  const { id: c, rawId: l, response: u, type: g } = a;
  let p;
  return u.userHandle && (p = G(u.userHandle)), {
    id: c,
    rawId: G(l),
    response: {
      authenticatorData: G(u.authenticatorData),
      clientDataJSON: G(u.clientDataJSON),
      signature: G(u.signature),
      userHandle: p
    },
    type: g,
    clientExtensionResults: a.getClientExtensionResults(),
    authenticatorAttachment: xe(a.authenticatorAttachment)
  };
}
function $n(e, t, n) {
  return e & t ^ ~e & n;
}
function Vn(e, t, n) {
  return e & t ^ e & n ^ t & n;
}
class Gn {
  blockLen;
  outputLen;
  padOffset;
  isLE;
  // For partial updates less than block size
  buffer;
  view;
  finished = !1;
  length = 0;
  pos = 0;
  destroyed = !1;
  constructor(t, n, r, s) {
    this.blockLen = t, this.outputLen = n, this.padOffset = r, this.isLE = s, this.buffer = new Uint8Array(t), this.view = Tt(this.buffer);
  }
  update(t) {
    ct(this), C(t);
    const { view: n, buffer: r, blockLen: s } = this, o = t.length;
    for (let i = 0; i < o; ) {
      const a = Math.min(s - this.pos, o - i);
      if (a === s) {
        const c = Tt(t);
        for (; s <= o - i; i += s)
          this.process(c, i);
        continue;
      }
      r.set(t.subarray(i, i + a), this.pos), this.pos += a, i += a, this.pos === s && (this.process(n, 0), this.pos = 0);
    }
    return this.length += t.length, this.roundClean(), this;
  }
  digestInto(t) {
    ct(this), fe(t, this), this.finished = !0;
    const { buffer: n, view: r, blockLen: s, isLE: o } = this;
    let { pos: i } = this;
    n[i++] = 128, ut(this.buffer.subarray(i)), this.padOffset > s - i && (this.process(r, 0), i = 0);
    for (let g = i; g < s; g++)
      n[g] = 0;
    r.setBigUint64(s - 8, BigInt(this.length * 8), o), this.process(r, 0);
    const a = Tt(t), c = this.outputLen;
    if (c % 4)
      throw new Error("_sha2: outputLen must be aligned to 32bit");
    const l = c / 4, u = this.get();
    if (l > u.length)
      throw new Error("_sha2: outputLen bigger than state");
    for (let g = 0; g < l; g++)
      a.setUint32(4 * g, u[g], o);
  }
  digest() {
    const { buffer: t, outputLen: n } = this;
    this.digestInto(t);
    const r = t.slice(0, n);
    return this.destroy(), r;
  }
  _cloneInto(t) {
    t ||= new this.constructor(), t.set(...this.get());
    const { blockLen: n, buffer: r, length: s, finished: o, destroyed: i, pos: a } = this;
    return t.destroyed = i, t.finished = o, t.length = s, t.pos = a, s % n && t.buffer.set(r), t;
  }
  clone() {
    return this._cloneInto();
  }
}
const X = /* @__PURE__ */ Uint32Array.from([
  1779033703,
  3144134277,
  1013904242,
  2773480762,
  1359893119,
  2600822924,
  528734635,
  1541459225
]), Zn = /* @__PURE__ */ Uint32Array.from([
  1116352408,
  1899447441,
  3049323471,
  3921009573,
  961987163,
  1508970993,
  2453635748,
  2870763221,
  3624381080,
  310598401,
  607225278,
  1426881987,
  1925078388,
  2162078206,
  2614888103,
  3248222580,
  3835390401,
  4022224774,
  264347078,
  604807628,
  770255983,
  1249150122,
  1555081692,
  1996064986,
  2554220882,
  2821834349,
  2952996808,
  3210313671,
  3336571891,
  3584528711,
  113926993,
  338241895,
  666307205,
  773529912,
  1294757372,
  1396182291,
  1695183700,
  1986661051,
  2177026350,
  2456956037,
  2730485921,
  2820302411,
  3259730800,
  3345764771,
  3516065817,
  3600352804,
  4094571909,
  275423344,
  430227734,
  506948616,
  659060556,
  883997877,
  958139571,
  1322822218,
  1537002063,
  1747873779,
  1955562222,
  2024104815,
  2227730452,
  2361852424,
  2428436474,
  2756734187,
  3204031479,
  3329325298
]), J = /* @__PURE__ */ new Uint32Array(64);
class Yn extends Gn {
  constructor(t) {
    super(64, t, 8, !1);
  }
  get() {
    const { A: t, B: n, C: r, D: s, E: o, F: i, G: a, H: c } = this;
    return [t, n, r, s, o, i, a, c];
  }
  // prettier-ignore
  set(t, n, r, s, o, i, a, c) {
    this.A = t | 0, this.B = n | 0, this.C = r | 0, this.D = s | 0, this.E = o | 0, this.F = i | 0, this.G = a | 0, this.H = c | 0;
  }
  process(t, n) {
    for (let g = 0; g < 16; g++, n += 4)
      J[g] = t.getUint32(n, !1);
    for (let g = 16; g < 64; g++) {
      const p = J[g - 15], y = J[g - 2], A = Y(p, 7) ^ Y(p, 18) ^ p >>> 3, B = Y(y, 17) ^ Y(y, 19) ^ y >>> 10;
      J[g] = B + J[g - 7] + A + J[g - 16] | 0;
    }
    let { A: r, B: s, C: o, D: i, E: a, F: c, G: l, H: u } = this;
    for (let g = 0; g < 64; g++) {
      const p = Y(a, 6) ^ Y(a, 11) ^ Y(a, 25), y = u + p + $n(a, c, l) + Zn[g] + J[g] | 0, B = (Y(r, 2) ^ Y(r, 13) ^ Y(r, 22)) + Vn(r, s, o) | 0;
      u = l, l = c, c = a, a = i + y | 0, i = o, o = s, s = r, r = y + B | 0;
    }
    r = r + this.A | 0, s = s + this.B | 0, o = o + this.C | 0, i = i + this.D | 0, a = a + this.E | 0, c = c + this.F | 0, l = l + this.G | 0, u = u + this.H | 0, this.set(r, s, o, i, a, c, l, u);
  }
  roundClean() {
    ut(J);
  }
  destroy() {
    this.set(0, 0, 0, 0, 0, 0, 0, 0), ut(this.buffer);
  }
}
class Fn extends Yn {
  // We cannot use array here since array allows indexing by variable
  // which means optimizer/compiler cannot use registers.
  A = X[0] | 0;
  B = X[1] | 0;
  C = X[2] | 0;
  D = X[3] | 0;
  E = X[4] | 0;
  F = X[5] | 0;
  G = X[6] | 0;
  H = X[7] | 0;
  constructor() {
    super(32);
  }
}
const zn = /* @__PURE__ */ ge(
  () => new Fn(),
  /* @__PURE__ */ pe(1)
);
/*! noble-curves - MIT License (c) 2022 Paul Miller (paulmillr.com) */
const Vt = /* @__PURE__ */ BigInt(0), Ht = /* @__PURE__ */ BigInt(1);
function Rt(e, t = "") {
  if (typeof e != "boolean") {
    const n = t && `"${t}" `;
    throw new Error(n + "expected boolean, got type=" + typeof e);
  }
  return e;
}
function Ie(e) {
  if (typeof e == "bigint") {
    if (!St(e))
      throw new Error("positive bigint expected, got " + e);
  } else
    W(e);
  return e;
}
function mt(e) {
  const t = Ie(e).toString(16);
  return t.length & 1 ? "0" + t : t;
}
function ve(e) {
  if (typeof e != "string")
    throw new Error("hex string expected, got " + typeof e);
  return e === "" ? Vt : BigInt("0x" + e);
}
function vt(e) {
  return ve(It(e));
}
function _e(e) {
  return ve(It(Wn(C(e)).reverse()));
}
function Gt(e, t) {
  W(t), e = Ie(e);
  const n = At(e.toString(16).padStart(t * 2, "0"));
  if (n.length !== t)
    throw new Error("number too large");
  return n;
}
function Oe(e, t) {
  return Gt(e, t).reverse();
}
function Wn(e) {
  return Uint8Array.from(e);
}
const St = (e) => typeof e == "bigint" && Vt <= e;
function Xn(e, t, n) {
  return St(e) && St(t) && St(n) && t <= e && e < n;
}
function Jn(e, t, n, r) {
  if (!Xn(t, n, r))
    throw new Error("expected valid " + e + ": " + n + " <= n < " + r + ", got " + t);
}
function Qn(e) {
  let t;
  for (t = 0; e > Vt; e >>= Ht, t += 1)
    ;
  return t;
}
const Zt = (e) => (Ht << BigInt(e)) - Ht;
function tr(e, t, n) {
  if (W(e, "hashLen"), W(t, "qByteLen"), typeof n != "function")
    throw new Error("hmacFn must be a function");
  const r = (I) => new Uint8Array(I), s = Uint8Array.of(), o = Uint8Array.of(0), i = Uint8Array.of(1), a = 1e3;
  let c = r(e), l = r(e), u = 0;
  const g = () => {
    c.fill(1), l.fill(0), u = 0;
  }, p = (...I) => n(l, et(c, ...I)), y = (I = s) => {
    l = p(o, I), c = p(), I.length !== 0 && (l = p(i, I), c = p());
  }, A = () => {
    if (u++ >= a)
      throw new Error("drbg: tried max amount of iterations");
    let I = 0;
    const P = [];
    for (; I < t; ) {
      c = p();
      const M = c.slice();
      P.push(M), I += c.length;
    }
    return et(...P);
  };
  return (I, P) => {
    g(), y(I);
    let M;
    for (; !(M = P(A())); )
      y();
    return g(), M;
  };
}
function Yt(e, t = {}, n = {}) {
  if (!e || typeof e != "object")
    throw new Error("expected valid options object");
  function r(o, i, a) {
    const c = e[o];
    if (a && c === void 0)
      return;
    const l = typeof c;
    if (l !== i || c === null)
      throw new Error(`param "${o}" is invalid: expected ${i}, got ${l}`);
  }
  const s = (o, i) => Object.entries(o).forEach(([a, c]) => r(a, c, i));
  s(t, !1), s(n, !0);
}
function re(e) {
  const t = /* @__PURE__ */ new WeakMap();
  return (n, ...r) => {
    const s = t.get(n);
    if (s !== void 0)
      return s;
    const o = e(n, ...r);
    return t.set(n, o), o;
  };
}
/*! noble-curves - MIT License (c) 2022 Paul Miller (paulmillr.com) */
const j = /* @__PURE__ */ BigInt(0), q = /* @__PURE__ */ BigInt(1), rt = /* @__PURE__ */ BigInt(2), Be = /* @__PURE__ */ BigInt(3), Te = /* @__PURE__ */ BigInt(4), Ne = /* @__PURE__ */ BigInt(5), er = /* @__PURE__ */ BigInt(7), Ce = /* @__PURE__ */ BigInt(8), nr = /* @__PURE__ */ BigInt(9), Le = /* @__PURE__ */ BigInt(16);
function V(e, t) {
  const n = e % t;
  return n >= j ? n : t + n;
}
function se(e, t) {
  if (e === j)
    throw new Error("invert: expected non-zero number");
  if (t <= j)
    throw new Error("invert: expected positive modulus, got " + t);
  let n = V(e, t), r = t, s = j, o = q;
  for (; n !== j; ) {
    const a = r / n, c = r % n, l = s - o * a;
    r = n, n = c, s = o, o = l;
  }
  if (r !== q)
    throw new Error("invert: does not exist");
  return V(s, t);
}
function Ft(e, t, n) {
  if (!e.eql(e.sqr(t), n))
    throw new Error("Cannot find square root");
}
function ke(e, t) {
  const n = (e.ORDER + q) / Te, r = e.pow(t, n);
  return Ft(e, r, t), r;
}
function rr(e, t) {
  const n = (e.ORDER - Ne) / Ce, r = e.mul(t, rt), s = e.pow(r, n), o = e.mul(t, s), i = e.mul(e.mul(o, rt), s), a = e.mul(o, e.sub(i, e.ONE));
  return Ft(e, a, t), a;
}
function sr(e) {
  const t = zt(e), n = Pe(e), r = n(t, t.neg(t.ONE)), s = n(t, r), o = n(t, t.neg(r)), i = (e + er) / Le;
  return (a, c) => {
    let l = a.pow(c, i), u = a.mul(l, r);
    const g = a.mul(l, s), p = a.mul(l, o), y = a.eql(a.sqr(u), c), A = a.eql(a.sqr(g), c);
    l = a.cmov(l, u, y), u = a.cmov(p, g, A);
    const B = a.eql(a.sqr(u), c), I = a.cmov(l, u, B);
    return Ft(a, I, c), I;
  };
}
function Pe(e) {
  if (e < Be)
    throw new Error("sqrt is not defined for small field");
  let t = e - q, n = 0;
  for (; t % rt === j; )
    t /= rt, n++;
  let r = rt;
  const s = zt(e);
  for (; ie(s, r) === 1; )
    if (r++ > 1e3)
      throw new Error("Cannot find square root: probably non-prime P");
  if (n === 1)
    return ke;
  let o = s.pow(r, t);
  const i = (t + q) / rt;
  return function(c, l) {
    if (c.is0(l))
      return l;
    if (ie(c, l) !== 1)
      throw new Error("Cannot find square root");
    let u = n, g = c.mul(c.ONE, o), p = c.pow(l, t), y = c.pow(l, i);
    for (; !c.eql(p, c.ONE); ) {
      if (c.is0(p))
        return c.ZERO;
      let A = 1, B = c.sqr(p);
      for (; !c.eql(B, c.ONE); )
        if (A++, B = c.sqr(B), A === u)
          throw new Error("Cannot find square root");
      const I = q << BigInt(u - A - 1), P = c.pow(g, I);
      u = A, g = c.sqr(P), p = c.mul(p, g), y = c.mul(y, P);
    }
    return y;
  };
}
function ir(e) {
  return e % Te === Be ? ke : e % Ce === Ne ? rr : e % Le === nr ? sr(e) : Pe(e);
}
const or = [
  "create",
  "isValid",
  "is0",
  "neg",
  "inv",
  "sqrt",
  "sqr",
  "eql",
  "add",
  "sub",
  "mul",
  "pow",
  "div",
  "addN",
  "subN",
  "mulN",
  "sqrN"
];
function ar(e) {
  const t = {
    ORDER: "bigint",
    BYTES: "number",
    BITS: "number"
  }, n = or.reduce((r, s) => (r[s] = "function", r), t);
  return Yt(e, n), e;
}
function cr(e, t, n) {
  if (n < j)
    throw new Error("invalid exponent, negatives unsupported");
  if (n === j)
    return e.ONE;
  if (n === q)
    return t;
  let r = e.ONE, s = t;
  for (; n > j; )
    n & q && (r = e.mul(r, s)), s = e.sqr(s), n >>= q;
  return r;
}
function Ue(e, t, n = !1) {
  const r = new Array(t.length).fill(n ? e.ZERO : void 0), s = t.reduce((i, a, c) => e.is0(a) ? i : (r[c] = i, e.mul(i, a)), e.ONE), o = e.inv(s);
  return t.reduceRight((i, a, c) => e.is0(a) ? i : (r[c] = e.mul(i, r[c]), e.mul(i, a)), o), r;
}
function ie(e, t) {
  const n = (e.ORDER - q) / rt, r = e.pow(t, n), s = e.eql(r, e.ONE), o = e.eql(r, e.ZERO), i = e.eql(r, e.neg(e.ONE));
  if (!s && !o && !i)
    throw new Error("invalid Legendre symbol result");
  return s ? 1 : o ? 0 : -1;
}
function ur(e, t) {
  t !== void 0 && W(t);
  const n = t !== void 0 ? t : e.toString(2).length, r = Math.ceil(n / 8);
  return { nBitLength: n, nByteLength: r };
}
class lr {
  ORDER;
  BITS;
  BYTES;
  isLE;
  ZERO = j;
  ONE = q;
  _lengths;
  _sqrt;
  // cached sqrt
  _mod;
  constructor(t, n = {}) {
    if (t <= j)
      throw new Error("invalid field: expected ORDER > 0, got " + t);
    let r;
    this.isLE = !1, n != null && typeof n == "object" && (typeof n.BITS == "number" && (r = n.BITS), typeof n.sqrt == "function" && (this.sqrt = n.sqrt), typeof n.isLE == "boolean" && (this.isLE = n.isLE), n.allowedLengths && (this._lengths = n.allowedLengths?.slice()), typeof n.modFromBytes == "boolean" && (this._mod = n.modFromBytes));
    const { nBitLength: s, nByteLength: o } = ur(t, r);
    if (o > 2048)
      throw new Error("invalid field: expected ORDER of <= 2048 bytes");
    this.ORDER = t, this.BITS = s, this.BYTES = o, this._sqrt = void 0, Object.preventExtensions(this);
  }
  create(t) {
    return V(t, this.ORDER);
  }
  isValid(t) {
    if (typeof t != "bigint")
      throw new Error("invalid field element: expected bigint, got " + typeof t);
    return j <= t && t < this.ORDER;
  }
  is0(t) {
    return t === j;
  }
  // is valid and invertible
  isValidNot0(t) {
    return !this.is0(t) && this.isValid(t);
  }
  isOdd(t) {
    return (t & q) === q;
  }
  neg(t) {
    return V(-t, this.ORDER);
  }
  eql(t, n) {
    return t === n;
  }
  sqr(t) {
    return V(t * t, this.ORDER);
  }
  add(t, n) {
    return V(t + n, this.ORDER);
  }
  sub(t, n) {
    return V(t - n, this.ORDER);
  }
  mul(t, n) {
    return V(t * n, this.ORDER);
  }
  pow(t, n) {
    return cr(this, t, n);
  }
  div(t, n) {
    return V(t * se(n, this.ORDER), this.ORDER);
  }
  // Same as above, but doesn't normalize
  sqrN(t) {
    return t * t;
  }
  addN(t, n) {
    return t + n;
  }
  subN(t, n) {
    return t - n;
  }
  mulN(t, n) {
    return t * n;
  }
  inv(t) {
    return se(t, this.ORDER);
  }
  sqrt(t) {
    return this._sqrt || (this._sqrt = ir(this.ORDER)), this._sqrt(this, t);
  }
  toBytes(t) {
    return this.isLE ? Oe(t, this.BYTES) : Gt(t, this.BYTES);
  }
  fromBytes(t, n = !1) {
    C(t);
    const { _lengths: r, BYTES: s, isLE: o, ORDER: i, _mod: a } = this;
    if (r) {
      if (!r.includes(t.length) || t.length > s)
        throw new Error("Field.fromBytes: expected " + r + " bytes, got " + t.length);
      const l = new Uint8Array(s);
      l.set(t, o ? 0 : l.length - t.length), t = l;
    }
    if (t.length !== s)
      throw new Error("Field.fromBytes: expected " + s + " bytes, got " + t.length);
    let c = o ? _e(t) : vt(t);
    if (a && (c = V(c, i)), !n && !this.isValid(c))
      throw new Error("invalid field element: outside of range 0..ORDER");
    return c;
  }
  // TODO: we don't need it here, move out to separate fn
  invertBatch(t) {
    return Ue(this, t);
  }
  // We can't move this out because Fp6, Fp12 implement it
  // and it's unclear what to return in there.
  cmov(t, n, r) {
    return r ? n : t;
  }
}
function zt(e, t = {}) {
  return new lr(e, t);
}
function De(e) {
  if (typeof e != "bigint")
    throw new Error("field order must be bigint");
  const t = e.toString(2).length;
  return Math.ceil(t / 8);
}
function He(e) {
  const t = De(e);
  return t + Math.ceil(t / 2);
}
function dr(e, t, n = !1) {
  C(e);
  const r = e.length, s = De(t), o = He(t);
  if (r < 16 || r < o || r > 1024)
    throw new Error("expected " + o + "-1024 bytes of input, got " + r);
  const i = n ? _e(e) : vt(e), a = V(i, t - q) + q;
  return n ? Oe(a, s) : Gt(a, s);
}
/*! noble-curves - MIT License (c) 2022 Paul Miller (paulmillr.com) */
const lt = /* @__PURE__ */ BigInt(0), st = /* @__PURE__ */ BigInt(1);
function xt(e, t) {
  const n = t.negate();
  return e ? n : t;
}
function oe(e, t) {
  const n = Ue(e.Fp, t.map((r) => r.Z));
  return t.map((r, s) => e.fromAffine(r.toAffine(n[s])));
}
function Ke(e, t) {
  if (!Number.isSafeInteger(e) || e <= 0 || e > t)
    throw new Error("invalid window size, expected [1.." + t + "], got W=" + e);
}
function Lt(e, t) {
  Ke(e, t);
  const n = Math.ceil(t / e) + 1, r = 2 ** (e - 1), s = 2 ** e, o = Zt(e), i = BigInt(e);
  return { windows: n, windowSize: r, mask: o, maxNumber: s, shiftBy: i };
}
function ae(e, t, n) {
  const { windowSize: r, mask: s, maxNumber: o, shiftBy: i } = n;
  let a = Number(e & s), c = e >> i;
  a > r && (a -= o, c += st);
  const l = t * r, u = l + Math.abs(a) - 1, g = a === 0, p = a < 0, y = t % 2 !== 0;
  return { nextN: c, offset: u, isZero: g, isNeg: p, isNegF: y, offsetF: l };
}
const kt = /* @__PURE__ */ new WeakMap(), qe = /* @__PURE__ */ new WeakMap();
function Pt(e) {
  return qe.get(e) || 1;
}
function ce(e) {
  if (e !== lt)
    throw new Error("invalid wNAF");
}
class fr {
  BASE;
  ZERO;
  Fn;
  bits;
  // Parametrized with a given Point class (not individual point)
  constructor(t, n) {
    this.BASE = t.BASE, this.ZERO = t.ZERO, this.Fn = t.Fn, this.bits = n;
  }
  // non-const time multiplication ladder
  _unsafeLadder(t, n, r = this.ZERO) {
    let s = t;
    for (; n > lt; )
      n & st && (r = r.add(s)), s = s.double(), n >>= st;
    return r;
  }
  /**
   * Creates a wNAF precomputation window. Used for caching.
   * Default window size is set by `utils.precompute()` and is equal to 8.
   * Number of precomputed points depends on the curve size:
   * 2^(𝑊−1) * (Math.ceil(𝑛 / 𝑊) + 1), where:
   * - 𝑊 is the window size
   * - 𝑛 is the bitlength of the curve order.
   * For a 256-bit curve and window size 8, the number of precomputed points is 128 * 33 = 4224.
   * @param point Point instance
   * @param W window size
   * @returns precomputed point tables flattened to a single array
   */
  precomputeWindow(t, n) {
    const { windows: r, windowSize: s } = Lt(n, this.bits), o = [];
    let i = t, a = i;
    for (let c = 0; c < r; c++) {
      a = i, o.push(a);
      for (let l = 1; l < s; l++)
        a = a.add(i), o.push(a);
      i = a.double();
    }
    return o;
  }
  /**
   * Implements ec multiplication using precomputed tables and w-ary non-adjacent form.
   * More compact implementation:
   * https://github.com/paulmillr/noble-secp256k1/blob/47cb1669b6e506ad66b35fe7d76132ae97465da2/index.ts#L502-L541
   * @returns real and fake (for const-time) points
   */
  wNAF(t, n, r) {
    if (!this.Fn.isValid(r))
      throw new Error("invalid scalar");
    let s = this.ZERO, o = this.BASE;
    const i = Lt(t, this.bits);
    for (let a = 0; a < i.windows; a++) {
      const { nextN: c, offset: l, isZero: u, isNeg: g, isNegF: p, offsetF: y } = ae(r, a, i);
      r = c, u ? o = o.add(xt(p, n[y])) : s = s.add(xt(g, n[l]));
    }
    return ce(r), { p: s, f: o };
  }
  /**
   * Implements ec unsafe (non const-time) multiplication using precomputed tables and w-ary non-adjacent form.
   * @param acc accumulator point to add result of multiplication
   * @returns point
   */
  wNAFUnsafe(t, n, r, s = this.ZERO) {
    const o = Lt(t, this.bits);
    for (let i = 0; i < o.windows && r !== lt; i++) {
      const { nextN: a, offset: c, isZero: l, isNeg: u } = ae(r, i, o);
      if (r = a, !l) {
        const g = n[c];
        s = s.add(u ? g.negate() : g);
      }
    }
    return ce(r), s;
  }
  getPrecomputes(t, n, r) {
    let s = kt.get(n);
    return s || (s = this.precomputeWindow(n, t), t !== 1 && (typeof r == "function" && (s = r(s)), kt.set(n, s))), s;
  }
  cached(t, n, r) {
    const s = Pt(t);
    return this.wNAF(s, this.getPrecomputes(s, t, r), n);
  }
  unsafe(t, n, r, s) {
    const o = Pt(t);
    return o === 1 ? this._unsafeLadder(t, n, s) : this.wNAFUnsafe(o, this.getPrecomputes(o, t, r), n, s);
  }
  // We calculate precomputes for elliptic curve point multiplication
  // using windowed method. This specifies window size and
  // stores precomputed values. Usually only base point would be precomputed.
  createCache(t, n) {
    Ke(n, this.bits), qe.set(t, n), kt.delete(t);
  }
  hasCache(t) {
    return Pt(t) !== 1;
  }
}
function hr(e, t, n, r) {
  let s = t, o = e.ZERO, i = e.ZERO;
  for (; n > lt || r > lt; )
    n & st && (o = o.add(s)), r & st && (i = i.add(s)), s = s.double(), n >>= st, r >>= st;
  return { p1: o, p2: i };
}
function ue(e, t, n) {
  if (t) {
    if (t.ORDER !== e)
      throw new Error("Field.ORDER must match order: Fp == p, Fn == n");
    return ar(t), t;
  } else
    return zt(e, { isLE: n });
}
function gr(e, t, n = {}, r) {
  if (r === void 0 && (r = e === "edwards"), !t || typeof t != "object")
    throw new Error(`expected valid ${e} CURVE object`);
  for (const c of ["p", "n", "h"]) {
    const l = t[c];
    if (!(typeof l == "bigint" && l > lt))
      throw new Error(`CURVE.${c} must be positive bigint`);
  }
  const s = ue(t.p, n.Fp, r), o = ue(t.n, n.Fn, r), a = ["Gx", "Gy", "a", "b"];
  for (const c of a)
    if (!s.isValid(t[c]))
      throw new Error(`CURVE.${c} must be valid field element of CURVE.Fp`);
  return t = Object.freeze(Object.assign({}, t)), { CURVE: t, Fp: s, Fn: o };
}
function wr(e, t) {
  return function(r) {
    const s = e(r);
    return { secretKey: s, publicKey: t(s) };
  };
}
class Me {
  oHash;
  iHash;
  blockLen;
  outputLen;
  finished = !1;
  destroyed = !1;
  constructor(t, n) {
    if (de(t), C(n, void 0, "key"), this.iHash = t.create(), typeof this.iHash.update != "function")
      throw new Error("Expected instance of class which extends utils.Hash");
    this.blockLen = this.iHash.blockLen, this.outputLen = this.iHash.outputLen;
    const r = this.blockLen, s = new Uint8Array(r);
    s.set(n.length > r ? t.create().update(n).digest() : n);
    for (let o = 0; o < s.length; o++)
      s[o] ^= 54;
    this.iHash.update(s), this.oHash = t.create();
    for (let o = 0; o < s.length; o++)
      s[o] ^= 106;
    this.oHash.update(s), ut(s);
  }
  update(t) {
    return ct(this), this.iHash.update(t), this;
  }
  digestInto(t) {
    ct(this), C(t, this.outputLen, "output"), this.finished = !0, this.iHash.digestInto(t), this.oHash.update(t), this.oHash.digestInto(t), this.destroy();
  }
  digest() {
    const t = new Uint8Array(this.oHash.outputLen);
    return this.digestInto(t), t;
  }
  _cloneInto(t) {
    t ||= Object.create(Object.getPrototypeOf(this), {});
    const { oHash: n, iHash: r, finished: s, destroyed: o, blockLen: i, outputLen: a } = this;
    return t = t, t.finished = s, t.destroyed = o, t.blockLen = i, t.outputLen = a, t.oHash = n._cloneInto(t.oHash), t.iHash = r._cloneInto(t.iHash), t;
  }
  clone() {
    return this._cloneInto();
  }
  destroy() {
    this.destroyed = !0, this.oHash.destroy(), this.iHash.destroy();
  }
}
const je = (e, t, n) => new Me(e, t).update(n).digest();
je.create = (e, t) => new Me(e, t);
/*! noble-curves - MIT License (c) 2022 Paul Miller (paulmillr.com) */
const le = (e, t) => (e + (e >= 0 ? t : -t) / $e) / t;
function pr(e, t, n) {
  const [[r, s], [o, i]] = t, a = le(i * e, n), c = le(-s * e, n);
  let l = e - a * r - c * o, u = -a * s - c * i;
  const g = l < z, p = u < z;
  g && (l = -l), p && (u = -u);
  const y = Zt(Math.ceil(Qn(n) / 2)) + at;
  if (l < z || l >= y || u < z || u >= y)
    throw new Error("splitScalar (endomorphism): failed, k=" + e);
  return { k1neg: g, k1: l, k2neg: p, k2: u };
}
function Kt(e) {
  if (!["compact", "recovered", "der"].includes(e))
    throw new Error('Signature format must be "compact", "recovered", or "der"');
  return e;
}
function Ut(e, t) {
  const n = {};
  for (let r of Object.keys(t))
    n[r] = e[r] === void 0 ? t[r] : e[r];
  return Rt(n.lowS, "lowS"), Rt(n.prehash, "prehash"), n.format !== void 0 && Kt(n.format), n;
}
class yr extends Error {
  constructor(t = "") {
    super(t);
  }
}
const Q = {
  // asn.1 DER encoding utils
  Err: yr,
  // Basic building block is TLV (Tag-Length-Value)
  _tlv: {
    encode: (e, t) => {
      const { Err: n } = Q;
      if (e < 0 || e > 256)
        throw new n("tlv.encode: wrong tag");
      if (t.length & 1)
        throw new n("tlv.encode: unpadded data");
      const r = t.length / 2, s = mt(r);
      if (s.length / 2 & 128)
        throw new n("tlv.encode: long form length too big");
      const o = r > 127 ? mt(s.length / 2 | 128) : "";
      return mt(e) + o + s + t;
    },
    // v - value, l - left bytes (unparsed)
    decode(e, t) {
      const { Err: n } = Q;
      let r = 0;
      if (e < 0 || e > 256)
        throw new n("tlv.encode: wrong tag");
      if (t.length < 2 || t[r++] !== e)
        throw new n("tlv.decode: wrong tlv");
      const s = t[r++], o = !!(s & 128);
      let i = 0;
      if (!o)
        i = s;
      else {
        const c = s & 127;
        if (!c)
          throw new n("tlv.decode(long): indefinite length not supported");
        if (c > 4)
          throw new n("tlv.decode(long): byte length is too big");
        const l = t.subarray(r, r + c);
        if (l.length !== c)
          throw new n("tlv.decode: length bytes not complete");
        if (l[0] === 0)
          throw new n("tlv.decode(long): zero leftmost byte");
        for (const u of l)
          i = i << 8 | u;
        if (r += c, i < 128)
          throw new n("tlv.decode(long): not minimal encoding");
      }
      const a = t.subarray(r, r + i);
      if (a.length !== i)
        throw new n("tlv.decode: wrong value length");
      return { v: a, l: t.subarray(r + i) };
    }
  },
  // https://crypto.stackexchange.com/a/57734 Leftmost bit of first byte is 'negative' flag,
  // since we always use positive integers here. It must always be empty:
  // - add zero byte if exists
  // - if next byte doesn't have a flag, leading zero is not allowed (minimal encoding)
  _int: {
    encode(e) {
      const { Err: t } = Q;
      if (e < z)
        throw new t("integer: negative integers are not allowed");
      let n = mt(e);
      if (Number.parseInt(n[0], 16) & 8 && (n = "00" + n), n.length & 1)
        throw new t("unexpected DER parsing assertion: unpadded hex");
      return n;
    },
    decode(e) {
      const { Err: t } = Q;
      if (e[0] & 128)
        throw new t("invalid signature integer: negative");
      if (e[0] === 0 && !(e[1] & 128))
        throw new t("invalid signature integer: unnecessary leading zero");
      return vt(e);
    }
  },
  toSig(e) {
    const { Err: t, _int: n, _tlv: r } = Q, s = C(e, void 0, "signature"), { v: o, l: i } = r.decode(48, s);
    if (i.length)
      throw new t("invalid signature: left bytes after parsing");
    const { v: a, l: c } = r.decode(2, o), { v: l, l: u } = r.decode(2, c);
    if (u.length)
      throw new t("invalid signature: left bytes after parsing");
    return { r: n.decode(a), s: n.decode(l) };
  },
  hexFromSig(e) {
    const { _tlv: t, _int: n } = Q, r = t.encode(2, n.encode(e.r)), s = t.encode(2, n.encode(e.s)), o = r + s;
    return t.encode(48, o);
  }
}, z = BigInt(0), at = BigInt(1), $e = BigInt(2), bt = BigInt(3), mr = BigInt(4);
function br(e, t = {}) {
  const n = gr("weierstrass", e, t), { Fp: r, Fn: s } = n;
  let o = n.CURVE;
  const { h: i, n: a } = o;
  Yt(t, {}, {
    allowInfinityPoint: "boolean",
    clearCofactor: "function",
    isTorsionFree: "function",
    fromBytes: "function",
    toBytes: "function",
    endo: "object"
  });
  const { endo: c } = t;
  if (c && (!r.is0(o.a) || typeof c.beta != "bigint" || !Array.isArray(c.basises)))
    throw new Error('invalid endo: expected "beta": bigint and "basises": array');
  const l = Ge(r, s);
  function u() {
    if (!r.isOdd)
      throw new Error("compression is not supported: Field does not have .isOdd()");
  }
  function g(_, h, f) {
    const { x: d, y: w } = h.toAffine(), b = r.toBytes(d);
    if (Rt(f, "isCompressed"), f) {
      u();
      const S = !r.isOdd(w);
      return et(Ve(S), b);
    } else
      return et(Uint8Array.of(4), b, r.toBytes(w));
  }
  function p(_) {
    C(_, void 0, "Point");
    const { publicKey: h, publicKeyUncompressed: f } = l, d = _.length, w = _[0], b = _.subarray(1);
    if (d === h && (w === 2 || w === 3)) {
      const S = r.fromBytes(b);
      if (!r.isValid(S))
        throw new Error("bad point: is not on curve, wrong x");
      const E = B(S);
      let m;
      try {
        m = r.sqrt(E);
      } catch (U) {
        const T = U instanceof Error ? ": " + U.message : "";
        throw new Error("bad point: is not on curve, sqrt error" + T);
      }
      u();
      const R = r.isOdd(m);
      return (w & 1) === 1 !== R && (m = r.neg(m)), { x: S, y: m };
    } else if (d === f && w === 4) {
      const S = r.BYTES, E = r.fromBytes(b.subarray(0, S)), m = r.fromBytes(b.subarray(S, S * 2));
      if (!I(E, m))
        throw new Error("bad point: is not on curve");
      return { x: E, y: m };
    } else
      throw new Error(`bad point: got length ${d}, expected compressed=${h} or uncompressed=${f}`);
  }
  const y = t.toBytes || g, A = t.fromBytes || p;
  function B(_) {
    const h = r.sqr(_), f = r.mul(h, _);
    return r.add(r.add(f, r.mul(_, o.a)), o.b);
  }
  function I(_, h) {
    const f = r.sqr(h), d = B(_);
    return r.eql(f, d);
  }
  if (!I(o.Gx, o.Gy))
    throw new Error("bad curve params: generator point");
  const P = r.mul(r.pow(o.a, bt), mr), M = r.mul(r.sqr(o.b), BigInt(27));
  if (r.is0(r.add(P, M)))
    throw new Error("bad curve params: a or b");
  function ot(_, h, f = !1) {
    if (!r.isValid(h) || f && r.is0(h))
      throw new Error(`bad point coordinate ${_}`);
    return h;
  }
  function $(_) {
    if (!(_ instanceof O))
      throw new Error("Weierstrass Point expected");
  }
  function dt(_) {
    if (!c || !c.basises)
      throw new Error("no endo");
    return pr(_, c.basises, s.ORDER);
  }
  const ft = re((_, h) => {
    const { X: f, Y: d, Z: w } = _;
    if (r.eql(w, r.ONE))
      return { x: f, y: d };
    const b = _.is0();
    h == null && (h = b ? r.ONE : r.inv(w));
    const S = r.mul(f, h), E = r.mul(d, h), m = r.mul(w, h);
    if (b)
      return { x: r.ZERO, y: r.ZERO };
    if (!r.eql(m, r.ONE))
      throw new Error("invZ was invalid");
    return { x: S, y: E };
  }), _t = re((_) => {
    if (_.is0()) {
      if (t.allowInfinityPoint && !r.is0(_.Y))
        return;
      throw new Error("bad point: ZERO");
    }
    const { x: h, y: f } = _.toAffine();
    if (!r.isValid(h) || !r.isValid(f))
      throw new Error("bad point: x or y not field elements");
    if (!I(h, f))
      throw new Error("bad point: equation left != right");
    if (!_.isTorsionFree())
      throw new Error("bad point: not in prime-order subgroup");
    return !0;
  });
  function ht(_, h, f, d, w) {
    return f = new O(r.mul(f.X, _), f.Y, f.Z), h = xt(d, h), f = xt(w, f), h.add(f);
  }
  class O {
    // base / generator point
    static BASE = new O(o.Gx, o.Gy, r.ONE);
    // zero / infinity / identity point
    static ZERO = new O(r.ZERO, r.ONE, r.ZERO);
    // 0, 1, 0
    // math field
    static Fp = r;
    // scalar field
    static Fn = s;
    X;
    Y;
    Z;
    /** Does NOT validate if the point is valid. Use `.assertValidity()`. */
    constructor(h, f, d) {
      this.X = ot("x", h), this.Y = ot("y", f, !0), this.Z = ot("z", d), Object.freeze(this);
    }
    static CURVE() {
      return o;
    }
    /** Does NOT validate if the point is valid. Use `.assertValidity()`. */
    static fromAffine(h) {
      const { x: f, y: d } = h || {};
      if (!h || !r.isValid(f) || !r.isValid(d))
        throw new Error("invalid affine point");
      if (h instanceof O)
        throw new Error("projective point not allowed");
      return r.is0(f) && r.is0(d) ? O.ZERO : new O(f, d, r.ONE);
    }
    static fromBytes(h) {
      const f = O.fromAffine(A(C(h, void 0, "point")));
      return f.assertValidity(), f;
    }
    static fromHex(h) {
      return O.fromBytes(At(h));
    }
    get x() {
      return this.toAffine().x;
    }
    get y() {
      return this.toAffine().y;
    }
    /**
     *
     * @param windowSize
     * @param isLazy true will defer table computation until the first multiplication
     * @returns
     */
    precompute(h = 8, f = !0) {
      return nt.createCache(this, h), f || this.multiply(bt), this;
    }
    // TODO: return `this`
    /** A point on curve is valid if it conforms to equation. */
    assertValidity() {
      _t(this);
    }
    hasEvenY() {
      const { y: h } = this.toAffine();
      if (!r.isOdd)
        throw new Error("Field doesn't support isOdd");
      return !r.isOdd(h);
    }
    /** Compare one point to another. */
    equals(h) {
      $(h);
      const { X: f, Y: d, Z: w } = this, { X: b, Y: S, Z: E } = h, m = r.eql(r.mul(f, E), r.mul(b, w)), R = r.eql(r.mul(d, E), r.mul(S, w));
      return m && R;
    }
    /** Flips point to one corresponding to (x, -y) in Affine coordinates. */
    negate() {
      return new O(this.X, r.neg(this.Y), this.Z);
    }
    // Renes-Costello-Batina exception-free doubling formula.
    // There is 30% faster Jacobian formula, but it is not complete.
    // https://eprint.iacr.org/2015/1060, algorithm 3
    // Cost: 8M + 3S + 3*a + 2*b3 + 15add.
    double() {
      const { a: h, b: f } = o, d = r.mul(f, bt), { X: w, Y: b, Z: S } = this;
      let E = r.ZERO, m = r.ZERO, R = r.ZERO, x = r.mul(w, w), U = r.mul(b, b), T = r.mul(S, S), v = r.mul(w, b);
      return v = r.add(v, v), R = r.mul(w, S), R = r.add(R, R), E = r.mul(h, R), m = r.mul(d, T), m = r.add(E, m), E = r.sub(U, m), m = r.add(U, m), m = r.mul(E, m), E = r.mul(v, E), R = r.mul(d, R), T = r.mul(h, T), v = r.sub(x, T), v = r.mul(h, v), v = r.add(v, R), R = r.add(x, x), x = r.add(R, x), x = r.add(x, T), x = r.mul(x, v), m = r.add(m, x), T = r.mul(b, S), T = r.add(T, T), x = r.mul(T, v), E = r.sub(E, x), R = r.mul(T, U), R = r.add(R, R), R = r.add(R, R), new O(E, m, R);
    }
    // Renes-Costello-Batina exception-free addition formula.
    // There is 30% faster Jacobian formula, but it is not complete.
    // https://eprint.iacr.org/2015/1060, algorithm 1
    // Cost: 12M + 0S + 3*a + 3*b3 + 23add.
    add(h) {
      $(h);
      const { X: f, Y: d, Z: w } = this, { X: b, Y: S, Z: E } = h;
      let m = r.ZERO, R = r.ZERO, x = r.ZERO;
      const U = o.a, T = r.mul(o.b, bt);
      let v = r.mul(f, b), L = r.mul(d, S), D = r.mul(w, E), Z = r.add(f, d), k = r.add(b, S);
      Z = r.mul(Z, k), k = r.add(v, L), Z = r.sub(Z, k), k = r.add(f, w);
      let K = r.add(b, E);
      return k = r.mul(k, K), K = r.add(v, D), k = r.sub(k, K), K = r.add(d, w), m = r.add(S, E), K = r.mul(K, m), m = r.add(L, D), K = r.sub(K, m), x = r.mul(U, k), m = r.mul(T, D), x = r.add(m, x), m = r.sub(L, x), x = r.add(L, x), R = r.mul(m, x), L = r.add(v, v), L = r.add(L, v), D = r.mul(U, D), k = r.mul(T, k), L = r.add(L, D), D = r.sub(v, D), D = r.mul(U, D), k = r.add(k, D), v = r.mul(L, k), R = r.add(R, v), v = r.mul(K, k), m = r.mul(Z, m), m = r.sub(m, v), v = r.mul(Z, L), x = r.mul(K, x), x = r.add(x, v), new O(m, R, x);
    }
    subtract(h) {
      return this.add(h.negate());
    }
    is0() {
      return this.equals(O.ZERO);
    }
    /**
     * Constant time multiplication.
     * Uses wNAF method. Windowed method may be 10% faster,
     * but takes 2x longer to generate and consumes 2x memory.
     * Uses precomputes when available.
     * Uses endomorphism for Koblitz curves.
     * @param scalar by which the point would be multiplied
     * @returns New point
     */
    multiply(h) {
      const { endo: f } = t;
      if (!s.isValidNot0(h))
        throw new Error("invalid scalar: out of range");
      let d, w;
      const b = (S) => nt.cached(this, S, (E) => oe(O, E));
      if (f) {
        const { k1neg: S, k1: E, k2neg: m, k2: R } = dt(h), { p: x, f: U } = b(E), { p: T, f: v } = b(R);
        w = U.add(v), d = ht(f.beta, x, T, S, m);
      } else {
        const { p: S, f: E } = b(h);
        d = S, w = E;
      }
      return oe(O, [d, w])[0];
    }
    /**
     * Non-constant-time multiplication. Uses double-and-add algorithm.
     * It's faster, but should only be used when you don't care about
     * an exposed secret key e.g. sig verification, which works over *public* keys.
     */
    multiplyUnsafe(h) {
      const { endo: f } = t, d = this;
      if (!s.isValid(h))
        throw new Error("invalid scalar: out of range");
      if (h === z || d.is0())
        return O.ZERO;
      if (h === at)
        return d;
      if (nt.hasCache(this))
        return this.multiply(h);
      if (f) {
        const { k1neg: w, k1: b, k2neg: S, k2: E } = dt(h), { p1: m, p2: R } = hr(O, d, b, E);
        return ht(f.beta, m, R, w, S);
      } else
        return nt.unsafe(d, h);
    }
    /**
     * Converts Projective point to affine (x, y) coordinates.
     * @param invertedZ Z^-1 (inverted zero) - optional, precomputation is useful for invertBatch
     */
    toAffine(h) {
      return ft(this, h);
    }
    /**
     * Checks whether Point is free of torsion elements (is in prime subgroup).
     * Always torsion-free for cofactor=1 curves.
     */
    isTorsionFree() {
      const { isTorsionFree: h } = t;
      return i === at ? !0 : h ? h(O, this) : nt.unsafe(this, a).is0();
    }
    clearCofactor() {
      const { clearCofactor: h } = t;
      return i === at ? this : h ? h(O, this) : this.multiplyUnsafe(i);
    }
    isSmallOrder() {
      return this.multiplyUnsafe(i).is0();
    }
    toBytes(h = !0) {
      return Rt(h, "isCompressed"), this.assertValidity(), y(O, this, h);
    }
    toHex(h = !0) {
      return It(this.toBytes(h));
    }
    toString() {
      return `<Point ${this.is0() ? "ZERO" : this.toHex()}>`;
    }
  }
  const wt = s.BITS, nt = new fr(O, t.endo ? Math.ceil(wt / 2) : wt);
  return O.BASE.precompute(8), O;
}
function Ve(e) {
  return Uint8Array.of(e ? 2 : 3);
}
function Ge(e, t) {
  return {
    secretKey: t.BYTES,
    publicKey: 1 + e.BYTES,
    publicKeyUncompressed: 1 + 2 * e.BYTES,
    publicKeyHasPrefix: !0,
    signature: 2 * t.BYTES
  };
}
function Er(e, t = {}) {
  const { Fn: n } = e, r = t.randomBytes || we, s = Object.assign(Ge(e.Fp, n), { seed: He(n.ORDER) });
  function o(y) {
    try {
      const A = n.fromBytes(y);
      return n.isValidNot0(A);
    } catch {
      return !1;
    }
  }
  function i(y, A) {
    const { publicKey: B, publicKeyUncompressed: I } = s;
    try {
      const P = y.length;
      return A === !0 && P !== B || A === !1 && P !== I ? !1 : !!e.fromBytes(y);
    } catch {
      return !1;
    }
  }
  function a(y = r(s.seed)) {
    return dr(C(y, s.seed, "seed"), n.ORDER);
  }
  function c(y, A = !0) {
    return e.BASE.multiply(n.fromBytes(y)).toBytes(A);
  }
  function l(y) {
    const { secretKey: A, publicKey: B, publicKeyUncompressed: I } = s;
    if (!Mt(y) || "_lengths" in n && n._lengths || A === B)
      return;
    const P = C(y, void 0, "key").length;
    return P === B || P === I;
  }
  function u(y, A, B = !0) {
    if (l(y) === !0)
      throw new Error("first arg must be private key");
    if (l(A) === !1)
      throw new Error("second arg must be public key");
    const I = n.fromBytes(y);
    return e.fromBytes(A).multiply(I).toBytes(B);
  }
  const g = {
    isValidSecretKey: o,
    isValidPublicKey: i,
    randomSecretKey: a
  }, p = wr(a, c);
  return Object.freeze({ getPublicKey: c, getSharedSecret: u, keygen: p, Point: e, utils: g, lengths: s });
}
function Sr(e, t, n = {}) {
  de(t), Yt(n, {}, {
    hmac: "function",
    lowS: "boolean",
    randomBytes: "function",
    bits2int: "function",
    bits2int_modN: "function"
  }), n = Object.assign({}, n);
  const r = n.randomBytes || we, s = n.hmac || ((f, d) => je(t, f, d)), { Fp: o, Fn: i } = e, { ORDER: a, BITS: c } = i, { keygen: l, getPublicKey: u, getSharedSecret: g, utils: p, lengths: y } = Er(e, n), A = {
    prehash: !0,
    lowS: typeof n.lowS == "boolean" ? n.lowS : !0,
    format: "compact",
    extraEntropy: !1
  }, B = a * $e < o.ORDER;
  function I(f) {
    const d = a >> at;
    return f > d;
  }
  function P(f, d) {
    if (!i.isValidNot0(d))
      throw new Error(`invalid signature ${f}: out of range 1..Point.Fn.ORDER`);
    return d;
  }
  function M() {
    if (B)
      throw new Error('"recovered" sig type is not supported for cofactor >2 curves');
  }
  function ot(f, d) {
    Kt(d);
    const w = y.signature, b = d === "compact" ? w : d === "recovered" ? w + 1 : void 0;
    return C(f, b);
  }
  class $ {
    r;
    s;
    recovery;
    constructor(d, w, b) {
      if (this.r = P("r", d), this.s = P("s", w), b != null) {
        if (M(), ![0, 1, 2, 3].includes(b))
          throw new Error("invalid recovery id");
        this.recovery = b;
      }
      Object.freeze(this);
    }
    static fromBytes(d, w = A.format) {
      ot(d, w);
      let b;
      if (w === "der") {
        const { r: R, s: x } = Q.toSig(C(d));
        return new $(R, x);
      }
      w === "recovered" && (b = d[0], w = "compact", d = d.subarray(1));
      const S = y.signature / 2, E = d.subarray(0, S), m = d.subarray(S, S * 2);
      return new $(i.fromBytes(E), i.fromBytes(m), b);
    }
    static fromHex(d, w) {
      return this.fromBytes(At(d), w);
    }
    assertRecovery() {
      const { recovery: d } = this;
      if (d == null)
        throw new Error("invalid recovery id: must be present");
      return d;
    }
    addRecoveryBit(d) {
      return new $(this.r, this.s, d);
    }
    recoverPublicKey(d) {
      const { r: w, s: b } = this, S = this.assertRecovery(), E = S === 2 || S === 3 ? w + a : w;
      if (!o.isValid(E))
        throw new Error("invalid recovery id: sig.r+curve.n != R.x");
      const m = o.toBytes(E), R = e.fromBytes(et(Ve((S & 1) === 0), m)), x = i.inv(E), U = ft(C(d, void 0, "msgHash")), T = i.create(-U * x), v = i.create(b * x), L = e.BASE.multiplyUnsafe(T).add(R.multiplyUnsafe(v));
      if (L.is0())
        throw new Error("invalid recovery: point at infinify");
      return L.assertValidity(), L;
    }
    // Signatures should be low-s, to prevent malleability.
    hasHighS() {
      return I(this.s);
    }
    toBytes(d = A.format) {
      if (Kt(d), d === "der")
        return At(Q.hexFromSig(this));
      const { r: w, s: b } = this, S = i.toBytes(w), E = i.toBytes(b);
      return d === "recovered" ? (M(), et(Uint8Array.of(this.assertRecovery()), S, E)) : et(S, E);
    }
    toHex(d) {
      return It(this.toBytes(d));
    }
  }
  const dt = n.bits2int || function(d) {
    if (d.length > 8192)
      throw new Error("input is too large");
    const w = vt(d), b = d.length * 8 - c;
    return b > 0 ? w >> BigInt(b) : w;
  }, ft = n.bits2int_modN || function(d) {
    return i.create(dt(d));
  }, _t = Zt(c);
  function ht(f) {
    return Jn("num < 2^" + c, f, z, _t), i.toBytes(f);
  }
  function O(f, d) {
    return C(f, void 0, "message"), d ? C(t(f), void 0, "prehashed message") : f;
  }
  function wt(f, d, w) {
    const { lowS: b, prehash: S, extraEntropy: E } = Ut(w, A);
    f = O(f, S);
    const m = ft(f), R = i.fromBytes(d);
    if (!i.isValidNot0(R))
      throw new Error("invalid private key");
    const x = [ht(R), ht(m)];
    if (E != null && E !== !1) {
      const L = E === !0 ? r(y.secretKey) : E;
      x.push(C(L, void 0, "extraEntropy"));
    }
    const U = et(...x), T = m;
    function v(L) {
      const D = dt(L);
      if (!i.isValidNot0(D))
        return;
      const Z = i.inv(D), k = e.BASE.multiply(D).toAffine(), K = i.create(k.x);
      if (K === z)
        return;
      const pt = i.create(Z * i.create(T + K * R));
      if (pt === z)
        return;
      let Wt = (k.x === K ? 0 : 2) | Number(k.y & at), Xt = pt;
      return b && I(pt) && (Xt = i.neg(pt), Wt ^= 1), new $(K, Xt, B ? void 0 : Wt);
    }
    return { seed: U, k2sig: v };
  }
  function nt(f, d, w = {}) {
    const { seed: b, k2sig: S } = wt(f, d, w);
    return tr(t.outputLen, i.BYTES, s)(b, S).toBytes(w.format);
  }
  function _(f, d, w, b = {}) {
    const { lowS: S, prehash: E, format: m } = Ut(b, A);
    if (w = C(w, void 0, "publicKey"), d = O(d, E), !Mt(f)) {
      const R = f instanceof $ ? ", use sig.toBytes()" : "";
      throw new Error("verify expects Uint8Array signature" + R);
    }
    ot(f, m);
    try {
      const R = $.fromBytes(f, m), x = e.fromBytes(w);
      if (S && R.hasHighS())
        return !1;
      const { r: U, s: T } = R, v = ft(d), L = i.inv(T), D = i.create(v * L), Z = i.create(U * L), k = e.BASE.multiplyUnsafe(D).add(x.multiplyUnsafe(Z));
      return k.is0() ? !1 : i.create(k.x) === U;
    } catch {
      return !1;
    }
  }
  function h(f, d, w = {}) {
    const { prehash: b } = Ut(w, A);
    return d = O(d, b), $.fromBytes(f, "recovered").recoverPublicKey(d).toBytes();
  }
  return Object.freeze({
    keygen: l,
    getPublicKey: u,
    getSharedSecret: g,
    utils: p,
    lengths: y,
    Point: e,
    sign: nt,
    verify: _,
    recoverPublicKey: h,
    Signature: $,
    hash: t
  });
}
/*! noble-curves - MIT License (c) 2022 Paul Miller (paulmillr.com) */
const Ar = {
  p: BigInt("0xffffffff00000001000000000000000000000000ffffffffffffffffffffffff"),
  n: BigInt("0xffffffff00000000ffffffffffffffffbce6faada7179e84f3b9cac2fc632551"),
  h: BigInt(1),
  a: BigInt("0xffffffff00000001000000000000000000000000fffffffffffffffffffffffc"),
  b: BigInt("0x5ac635d8aa3a93e7b3ebbd55769886bc651d06b0cc53b0f63bce3c3e27d2604b"),
  Gx: BigInt("0x6b17d1f2e12c4247f8bce6e563a440f277037d812deb33a0f4a13945d898c296"),
  Gy: BigInt("0x4fe342e2fe1a7f9b8ee7eb4a7c0f9e162bce33576b315ececbb6406837bf51f5")
}, Rr = /* @__PURE__ */ br(Ar), Dt = /* @__PURE__ */ Sr(Rr, zn);
function xr(e, t = "compact", n = "compact") {
  const r = Dt.Signature.fromBytes(e, t);
  if (!r.hasHighS()) return r.toBytes(n);
  const s = Dt.Point.Fn.neg(r.s), o = r.recovery != null ? r.recovery ^ 1 : void 0;
  return new Dt.Signature(r.r, s, o).toBytes(n);
}
async function Ir(e) {
  const t = e.endpoint || "https://api.caas.xyz";
  let n;
  const s = await (await fetch(`${t}/webauthn/register/options`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      username: e.userName,
      // User's wallet address used as username
      rpId: e.rpId
      // Relying Party ID (domain name)
    })
  })).json();
  if (s.error)
    throw new N(
      tt("Failed to create passkey", s.error),
      "CREATE_PASSKEY_ERROR",
      s.error
    );
  n = await Kn({
    optionsJSON: {
      ...s,
      user: {
        ...s.user,
        id: atob(s.user.id)
      }
    }
  });
  const i = await (await fetch(`${e.endpoint}/webauthn/register/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      userId: atob(s.user.id),
      username: s.user.name,
      // User's wallet address
      rpId: e.rpId,
      // Domain name for verification
      credential: n
      // Complete WebAuthn credential response
    })
  })).json();
  if (!i.success)
    throw new N(
      tt("Failed to verify passkey", i.error),
      "VERIFY_ERROR",
      i.error
    );
  return {
    credentialId: i.credentialId,
    // WebAuthn credential identifier
    publicKey: i.publicKey,
    // Processed public key in Aptos format
    address: i.address
    // Derived Aptos address for this passkey
  };
}
async function vr(e) {
  const { credentialId: t, publicKey: n, transaction: r, timeout: s, rpId: o, options: i } = e;
  if (!(n instanceof qt))
    throw new Error("Unsupported public key for passkey signing.");
  const a = i?.allowCredentials?.map((M) => M.id) ?? [t], c = We(r), l = kn(c), u = G(new Uint8Array(l).buffer), g = await jn({
    optionsJSON: {
      challenge: u,
      // SHA3 hash of transaction as challenge
      rpId: o,
      // Domain name for credential verification
      timeout: s,
      // Optional timeout for user interaction
      allowCredentials: a.map((M) => ({
        id: M,
        // Credential ID to use for signing
        type: "public-key"
        // WebAuthn credential type
      })),
      userVerification: "required"
      // Require biometric/PIN verification
    }
  }), { clientDataJSON: p, authenticatorData: y, signature: A } = g.response;
  let B = xr(new Uint8Array(it(A)), "der", "compact");
  const I = new Xe(
    B,
    // Normalized compact signature
    new Uint8Array(it(y)),
    // Authenticator data from WebAuthn
    new Uint8Array(it(p))
    // Client data JSON from WebAuthn
  );
  return new Je(
    new Qe(n),
    // Wrap the Secp256r1 public key
    new tn(I)
    // Wrap the WebAuthn signature
  );
}
var _r = /* @__PURE__ */ ((e) => (e.CREATE_PASSKEY = "create-passkey", e.SELECTED_PASSKEY_SIGNATURE = "selected-passkey-signature", e.WALLET_SIGNATURE = "wallet-signature", e.TRANSACTION_SUBMISSION = "transaction-submission", e))(_r || {});
class Or {
  /**
   * Initialize the Passkey module
   * @param core - CaasCore instance providing configuration and Aptos client
   */
  constructor(t) {
    this.core = t, this.transactionSimulator = new hn(this.core.Endpoint), this.loginManager = new fn(this.core);
  }
  /** Transaction simulator for handling multi-agent transactions */
  transactionSimulator;
  /** Login manager for handling user authentication */
  loginManager;
  /** Cached hostname from domain configuration */
  _hostname;
  /**
   * Get the login manager
   *
   * @returns LoginManager instance
   */
  get LoginManager() {
    return this.loginManager;
  }
  /**
   * Validate common arguments used across multiple methods
   * Ensures projectId and address are present and valid
   *
   * @param args - Object containing projectId and address to validate
   * @throws {PasskeyError} When validation fails
   */
  validateCommonArgs(t) {
    if (!t.projectId)
      throw new N("Project ID is required", "MISSING_PROJECT_ID");
    if (!t.address || !en.isValid({ input: t.address, strict: !0 }).valid)
      throw new N("Valid address is required", "INVALID_ADDRESS");
  }
  /**
   * Validate that core configuration contains required network and domain settings
   *
   * @throws {PasskeyError} When required configuration is missing
   */
  validateCoreConfig() {
    if (!this.core.Config.network)
      throw new N("Network configuration is required", "MISSING_NETWORK");
    if (!this.core.Config.domain)
      throw new N("Domain configuration is required", "MISSING_DOMAIN");
  }
  /**
   * Validate that a passkey object contains required properties
   *
   * @param passkey - PasskeyItem to validate
   * @throws {PasskeyError} When passkey is invalid or incomplete
   */
  validatePasskey(t) {
    if (!t.publicKey || !t.credentialId)
      throw new N("Valid passkey is required", "INVALID_PASSKEY");
  }
  /**
   * Extract and cache hostname from domain configuration
   * Uses caching to avoid repeated URL parsing
   *
   * @returns Domain hostname (e.g., 'example.com')
   * @throws {PasskeyError} When domain configuration is invalid
   */
  getHostname() {
    return this._hostname ? this._hostname : (this.validateCoreConfig(), this._hostname = new URL(this.core.Config.domain).hostname, this._hostname);
  }
  /**
   * Generate fully qualified contract function name for the current network
   *
   * @param functionName - Contract function name (e.g., 'initialize', 'register_when_exists')
   * @returns Fully qualified function name in format 'address::module::function'
   */
  getContractFunction(t) {
    return `${an.passkey["aptos-" + this.core.Config.network.toLowerCase()]}::passkey::${t}`;
  }
  /**
   * Create a new passkey using the backend service
   * Handles WebAuthn credential creation and backend registration
   *
   * @param address - User's wallet address to associate with the passkey
   * @param hostname - Domain hostname for the passkey
   * @returns Promise containing new passkey details (publicKey, credentialId, address)
   */
  async createNewPasskey(t, n) {
    return await Ir({
      rpId: n,
      userName: t,
      endpoint: this.core.Endpoint
    });
  }
  /**
   * Sign a multi-agent transaction using a passkey
   * Utilizes WebAuthn for secure transaction signing
   *
   * @param transaction - Multi-agent transaction to sign
   * @param passkey - Passkey to use for signing
   * @param hostname - Domain hostname for WebAuthn
   * @returns Promise resolving to account authenticator
   */
  async signTransactionWithPasskey(t, n, r) {
    return await vr({
      transaction: t,
      publicKey: new qt(n.publicKey),
      credentialId: n.credentialId,
      rpId: r
    });
  }
  /**
   * Obtain and validate sender authenticator from external signer
   * Ensures the signer function returns a valid authenticator
   *
   * @param signer - External signing function (e.g., from wallet)
   * @param transaction - Transaction to be signed
   * @returns Promise resolving to validated account authenticator
   * @throws {PasskeyError} When signer returns undefined/null
   */
  async getSenderAuthenticator(t, n) {
    const r = await t(n);
    if (!r)
      throw new N("Sender authenticator is required", "MISSING_SENDER_AUTH");
    return r;
  }
  /**
   * Build a multi-agent transaction with proper gas configuration
   * Combines user payload with secondary signers (passkeys, project signer)
   *
   * @param sender - Primary sender address
   * @param payload - Transaction payload data
   * @param secondarySignerAddresses - Additional signer addresses (passkeys, project signer)
   * @param options - Optional transaction configuration (gas limits, etc.)
   * @returns Promise resolving to built multi-agent transaction
   */
  async buildMultiAgentTransaction(t, n, r, s) {
    return await this.core.AptosClient.transaction.build.multiAgent({
      sender: t,
      data: n,
      secondarySignerAddresses: r,
      options: {
        maxGasAmount: cn.maxGasAmount,
        // default to 300 seconds
        expireTimestamp: Math.floor(Date.now() / 1e3) + 300,
        ...s
      }
    });
  }
  /**
   * Create and register the first passkey for a user account
   *
   * This method performs the complete end-to-end workflow for initializing
   * passkey authentication for a new user, including passkey creation,
   * transaction signing, and blockchain submission with progress tracking.
   *
   * Workflow:
   * 1. Creates a new WebAuthn passkey credential
   * 2. Signs the initialization transaction with user's wallet
   * 3. Submits the multi-agent transaction to the blockchain
   * 4. Waits for transaction confirmation
   *
   * @param args - Configuration for first passkey creation
   * @param args.address - User's wallet address
   * @param args.projectId - Project identifier
   * @param args.senderAuthenticatorSign - Function to sign the transaction
   * @param callbacks - Optional progress callbacks for step tracking
   * @returns Promise resolving to committed transaction result
   * @throws {PasskeyError} When validation fails or any step in the workflow fails
   */
  async createFirstPasskey(t, n) {
    this.validateCommonArgs(t), this.validateCoreConfig();
    const r = this.getHostname();
    n?.onStepStart?.(
      "create-passkey"
      /* CREATE_PASSKEY */
    );
    let s, o;
    try {
      const u = await this.createNewPasskey(t.address, r);
      s = u.publicKey, o = u.address, n?.onStepSuccess?.("create-passkey", u);
    } catch (u) {
      throw n?.onStepError?.("create-passkey", u), u;
    }
    const i = {
      function: this.getContractFunction("initialize"),
      typeArguments: [t.projectId],
      functionArguments: [o, s]
    }, a = await this.buildMultiAgentTransaction(t.address, i, [t.projectSignerAddress]);
    n?.onStepStart?.(
      "wallet-signature"
      /* WALLET_SIGNATURE */
    );
    let c;
    try {
      const { projectSenderAuthenticator: u } = await this.transactionSimulator.getProjectSigner(a);
      c = await this.getSenderAuthenticator(t.senderAuthenticatorSign, a), n?.onStepSuccess?.("wallet-signature", { senderAuthenticator: c, projectSenderAuthenticator: u });
    } catch (u) {
      throw n?.onStepError?.("wallet-signature", u), u;
    }
    n?.onStepStart?.(
      "transaction-submission"
      /* TRANSACTION_SUBMISSION */
    );
    let l;
    try {
      const { projectSenderAuthenticator: u } = await this.transactionSimulator.getProjectSigner(a);
      l = await this.core.AptosClient.transaction.submit.multiAgent({
        transaction: a,
        senderAuthenticator: c,
        additionalSignersAuthenticators: [u]
      }), n?.onStepSuccess?.("transaction-submission", l);
    } catch (u) {
      throw n?.onStepError?.("transaction-submission", u), u;
    }
    return l;
  }
  /**
   * Create and register an additional passkey for an existing account
   *
   * This method performs the complete end-to-end workflow for adding a new
   * passkey to an account that already has passkey authentication enabled.
   * Requires authorization from an existing passkey.
   *
   * Workflow:
   * 1. Creates a new WebAuthn passkey credential
   * 2. Signs the transaction with the selected existing passkey
   * 3. Signs the transaction with user's wallet
   * 4. Submits the multi-agent transaction to the blockchain
   * 5. Waits for transaction confirmation
   *
   * @param args - Configuration for additional passkey creation
   * @param args.address - User's wallet address
   * @param args.projectId - Project identifier
   * @param args.selectedPasskey - Existing passkey to authorize the addition
   * @param args.extraData - Optional additional data to store
   * @param args.senderAuthenticatorSign - Function to sign the transaction
   * @param callbacks - Optional progress callbacks for step tracking
   * @returns Promise resolving to committed transaction result
   * @throws {PasskeyError} When validation fails or any step in the workflow fails
   */
  async createAdditionalPasskey(t, n) {
    this.validateCommonArgs(t), this.validateCoreConfig(), this.validatePasskey(t.selectedPasskey);
    const r = this.getHostname();
    n?.onStepStart?.(
      "create-passkey"
      /* CREATE_PASSKEY */
    );
    let s, o;
    try {
      const p = await this.createNewPasskey(t.address, r);
      s = p.publicKey, o = p.address, n?.onStepSuccess?.("create-passkey", p);
    } catch (p) {
      throw n?.onStepError?.("create-passkey", p), p;
    }
    const i = {
      function: this.getContractFunction("register_when_exists"),
      typeArguments: [t.projectId],
      functionArguments: [o, s]
    }, a = this.transactionSimulator.restoreAddressFromPublicKey(
      t.selectedPasskey.publicKey
    ), c = await this.buildMultiAgentTransaction(t.address, i, [
      a,
      t.projectSignerAddress
    ]);
    n?.onStepStart?.(
      "selected-passkey-signature"
      /* SELECTED_PASSKEY_SIGNATURE */
    );
    let l;
    try {
      l = await this.signTransactionWithPasskey(c, t.selectedPasskey, r), n?.onStepSuccess?.("selected-passkey-signature", l);
    } catch (p) {
      throw n?.onStepError?.("selected-passkey-signature", p), p;
    }
    n?.onStepStart?.(
      "wallet-signature"
      /* WALLET_SIGNATURE */
    );
    let u;
    try {
      u = await this.getSenderAuthenticator(t.senderAuthenticatorSign, c), n?.onStepSuccess?.("wallet-signature", u);
    } catch (p) {
      throw n?.onStepError?.("wallet-signature", p), p;
    }
    n?.onStepStart?.(
      "transaction-submission"
      /* TRANSACTION_SUBMISSION */
    );
    let g;
    try {
      const { projectSenderAuthenticator: p } = await this.transactionSimulator.getProjectSigner(c);
      g = await this.core.AptosClient.transaction.submit.multiAgent({
        transaction: c,
        senderAuthenticator: u,
        additionalSignersAuthenticators: [l, p]
      }), n?.onStepSuccess?.("transaction-submission", g);
    } catch (p) {
      throw n?.onStepError?.("transaction-submission", p), p;
    }
    return g;
  }
  /**
   * Remove a passkey from an existing account with progress tracking
   *
   * This method performs the complete end-to-end workflow for removing
   * an existing passkey from a user account. The passkey being removed
   * must authorize its own removal for security purposes.
   *
   * Workflow:
   * 1. Signs the removal transaction with the passkey to be removed
   * 2. Signs the transaction with user's wallet
   * 3. Submits the multi-agent transaction to the blockchain
   * 4. Waits for transaction confirmation
   *
   * @param args - Configuration for passkey removal
   * @param args.address - User's wallet address
   * @param args.projectId - Project identifier
   * @param args.selectedPasskey - Passkey to be removed (must sign the removal)
   * @param args.senderAuthenticatorSign - Function to sign the transaction
   * @param callbacks - Optional progress callbacks for step tracking
   * @returns Promise resolving to committed transaction result
   * @throws {PasskeyError} When validation fails or any step in the workflow fails
   */
  async removePasskey(t, n) {
    this.validateCommonArgs(t), this.validateCoreConfig(), this.validatePasskey(t.selectedPasskey);
    const r = this.getHostname(), s = this.transactionSimulator.restoreAddressFromPublicKey(
      t.selectedPasskey.publicKey
    ), o = {
      function: this.getContractFunction("remove_passkey"),
      typeArguments: [t.projectId],
      functionArguments: [s]
    }, i = await this.buildMultiAgentTransaction(t.address, o, [
      s,
      t.projectSignerAddress
    ]);
    n?.onStepStart?.(
      "selected-passkey-signature"
      /* SELECTED_PASSKEY_SIGNATURE */
    );
    let a;
    try {
      a = await this.signTransactionWithPasskey(i, t.selectedPasskey, r), n?.onStepSuccess?.("selected-passkey-signature", a);
    } catch (u) {
      throw n?.onStepError?.("selected-passkey-signature", u), u;
    }
    n?.onStepStart?.(
      "wallet-signature"
      /* WALLET_SIGNATURE */
    );
    let c;
    try {
      c = await this.getSenderAuthenticator(t.senderAuthenticatorSign, i), n?.onStepSuccess?.("wallet-signature", c);
    } catch (u) {
      throw n?.onStepError?.("wallet-signature", u), u;
    }
    n?.onStepStart?.(
      "transaction-submission"
      /* TRANSACTION_SUBMISSION */
    );
    let l;
    try {
      const { projectSenderAuthenticator: u } = await this.transactionSimulator.getProjectSigner(i);
      l = await this.core.AptosClient.transaction.submit.multiAgent({
        transaction: i,
        senderAuthenticator: c,
        additionalSignersAuthenticators: [a, u]
      }), n?.onStepSuccess?.("transaction-submission", l);
    } catch (u) {
      throw n?.onStepError?.("transaction-submission", u), u;
    }
    return l;
  }
  /**
   * Check if a user address is registered in the passkey system
   * Queries the blockchain to determine registration status
   *
   * @param projectId - Project identifier to check registration for
   * @param address - User's wallet address to check
   * @returns Promise resolving to boolean indicating registration status
   * @throws {PasskeyError} When validation fails or view call fails
   */
  async isAddressRegistered(t, n) {
    this.validateCommonArgs({ projectId: t, address: n }), this.validateCoreConfig();
    const r = {
      function: this.getContractFunction("is_user_registered"),
      typeArguments: [t],
      functionArguments: [n]
    };
    return !!(await this.core.AptosClient.view({
      payload: r
    }))?.[0] || !1;
  }
  /**
   * Sign and submit an arbitrary transaction using a passkey
   * Allows passkeys to authorize any transaction on behalf of the user
   *
   * This method:
   * 1. Validates the passkey and address
   * 2. Builds multi-agent transaction with passkey as secondary signer
   * 3. Coordinates signing between user wallet and passkey
   * 4. Submits transaction to blockchain
   *
   * @param args - Configuration for transaction signing
   * @param args.payload - Transaction payload to execute
   * @param args.passkey - Passkey to use for signing
   * @param args.address - User's wallet address
   * @param args.options - Optional transaction configuration
   * @param args.senderAuthenticatorSign - Function to sign the transaction
   * @returns Promise resolving to committed transaction result
   * @throws {PasskeyError} When validation fails or transaction submission fails
   */
  async signAndSubmitTransactionWithPasskey(t) {
    if (!t.address)
      throw new N("Address is required", "MISSING_ADDRESS");
    this.validatePasskey(t.passkey), this.validateCoreConfig();
    const n = this.getHostname(), r = this.transactionSimulator.restoreAddressFromPublicKey(t.passkey.publicKey), s = await this.buildMultiAgentTransaction(
      t.address,
      t.payload,
      [r, t.projectSignerAddress],
      t.options
    ), o = await this.signTransactionWithPasskey(s, t.passkey, n), i = await this.getSenderAuthenticator(t.senderAuthenticatorSign, s), { projectSenderAuthenticator: a } = await this.transactionSimulator.getProjectSigner(s);
    return await this.core.AptosClient.transaction.submit.multiAgent({
      transaction: s,
      senderAuthenticator: i,
      additionalSignersAuthenticators: [o, a]
    });
  }
  /**
   * Get all passkeys associated with a user address for the current domain
   * Filters results to only include passkeys registered for the configured domain
   *
   * @param projectId - Project identifier to query passkeys for
   * @param address - User's wallet address to get passkeys for
   * @returns Promise resolving to array of PasskeyItem objects for this domain
   * @throws {PasskeyError} When validation fails or view call fails
   */
  async getPasskeysByAddress(t, n) {
    this.validateCommonArgs({ projectId: t, address: n }), this.validateCoreConfig();
    const r = this.getHostname(), s = await this.loginManager.getPasskeys({ rpId: r });
    return Array.isArray(s) ? s.sort((o, i) => i.createdAt.toString().localeCompare(o.createdAt.toString()) ?? 0) : [];
  }
  /**
   * Restore an Aptos address from a public key
   * Convenience method that delegates to transaction simulator
   *
   * @param publicKey - Secp256r1 public key in hex format
   * @returns Derived Aptos address as a string
   */
  restoreAddressFromPublicKey(t) {
    return this.transactionSimulator.restoreAddressFromPublicKey(t);
  }
}
class Br {
  config;
  passkey;
  aptosClient;
  endpoint;
  constructor(t) {
    if (this.config = {
      fullnode: un,
      network: nn.MAINNET,
      domain: "",
      ...t
    }, !this.config.domain)
      throw new Error("Domain is required -- CaasCore");
    this.aptosClient = new rn(
      new sn({
        fullnode: this.config.fullnode,
        network: this.config.network
      })
    ), this.endpoint = this.config.options?.endpoint || ln[this.config.network], this.passkey = new Or(this);
  }
  get Passkey() {
    return this.passkey;
  }
  get Config() {
    return this.config;
  }
  get AptosClient() {
    return this.aptosClient;
  }
  get Endpoint() {
    return this.endpoint;
  }
}
const Cr = (e) => new Br(e);
export {
  Br as CaasCore,
  N as PasskeyError,
  _r as PasskeyStepName,
  hn as TransactionSimulator,
  Cr as createCaasClient
};
